import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useGoogleSync } from '../hooks/useGoogleSync';
import { generarPDFHistoriaCompleta, generarPDFSesionIndividual, getProfesionalData } from '../utils/pdfHistoria';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
import {
  Calendar, Users, DollarSign, BarChart3, LogOut, Plus, Search, X,
  MessageCircle, Phone, Mail, Clock, ChevronLeft, ChevronRight,
  TrendingUp, Trash2, CheckCircle, AlertCircle, Printer, FileText,
  Activity, BookOpen, List, Upload, Download, FileCheck,
  User, Shield, KeyRound, UserPlus, Edit2, Video, Cake, Bell
} from 'lucide-react';

// ── Paleta ────────────────────────────────────────────────────
const C = {
  rosa:'#F5B8A0', rosaLight:'#FDF0EB', rosaMid:'#F0A88E',
  ocre:'#C4956A', ocreLight:'#F5E8D8', ocreDark:'#A07848',
  teal:'#2D4F4A', tealLight:'#E8F0EF', tealMid:'#3D6560',
  crema:'#FAF7F4', white:'#FFFFFF', gray:'#6B7280',
  border:'#E8D5C4', success:'#10B981', warning:'#F59E0B', danger:'#EF4444',
};

const btn = (v='primary', sm=false) => ({
  display:'inline-flex', alignItems:'center', gap:'0.4rem',
  padding: sm ? '0.35rem 0.8rem' : '0.6rem 1.2rem',
  borderRadius:'10px', border:'none', cursor:'pointer',
  fontSize: sm ? '0.78rem' : '0.9rem', fontWeight:600,
  fontFamily:"'MyriadPro',Arial,sans-serif", whiteSpace:'nowrap',
  transition:'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow:'0 2px 8px rgba(0,0,0,0.1)',
  ...(v==='primary'  && {background:`linear-gradient(135deg,${C.ocre},${C.ocreDark})`,color:'#fff'}),
  ...(v==='teal'     && {background:`linear-gradient(135deg,${C.teal},${C.tealMid})`,color:'#fff'}),
  ...(v==='ghost'    && {background:'transparent',color:C.teal,border:`1.5px solid ${C.border}`,boxShadow:'none'}),
  ...(v==='danger'   && {background:'#FEE2E2',color:C.danger,border:'none',boxShadow:'0 2px 6px rgba(239, 68, 68, 0.12)'}),
  ...(v==='success'  && {background:'#D1FAE5',color:'#065F46',border:'none',boxShadow:'0 2px 6px rgba(16, 185, 129, 0.12)'}),
  ...(v==='whatsapp' && {background:'linear-gradient(135deg,#25D366,#20ba58)',color:'#fff',border:'none',boxShadow:'0 4px 12px rgba(37, 211, 102, 0.25)'}),
  ...(v==='print'    && {background:C.tealLight,color:C.teal,border:`1px solid ${C.teal}33`,boxShadow:'none'}),
});
const card  = {background:C.white,borderRadius:'16px',boxShadow:'0 4px 20px rgba(45,79,74,0.1)',border:`1px solid ${C.border}`,transition:'all 0.3s ease'};
const badge = (col) => ({
  display:'inline-block', padding:'0.2rem 0.65rem', borderRadius:'20px', fontSize:'0.72rem', fontWeight:600,
  background: col==='green'?'#D1FAE5':col==='amber'?'#FEF3C7':col==='red'?'#FEE2E2':col==='blue'?C.tealLight:C.ocreLight,
  color:       col==='green'?'#065F46':col==='amber'?'#92400E':col==='red'?'#991B1B':col==='blue'?C.teal:C.ocreDark,
});
const inp = {width:'100%',padding:'0.6rem 0.9rem',border:`1.5px solid ${C.border}`,borderRadius:'10px',fontSize:'0.9rem',color:C.teal,outline:'none',fontFamily:"'MyriadPro',Arial,sans-serif",background:C.crema,boxSizing:'border-box'};

const TABS = [
  {id:'agenda',      icon:Calendar,   label:'Agenda',       rol:null},
  {id:'citas',       icon:List,       label:'Citas',        rol:null},
  {id:'pacientes',   icon:Users,      label:'Pacientes',    rol:null},
  {id:'cumpleanos',  icon:Cake,       label:'Cumpleaños',   rol:null},
  {id:'finanzas',    icon:DollarSign, label:'Finanzas',     rol:null},
  {id:'dashboard',   icon:BarChart3,  label:'Dashboard',    rol:null},
  {id:'usuarios',    icon:Shield,     label:'Usuarios',     rol:'admin'},
  {id:'perfil',      icon:User,       label:'Mi Perfil',    rol:null},
];
const MESES    = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_SEM = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

// Fecha local YYYY-MM-DD sin problemas de zona horaria
const localHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const fmt      = n  => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(n||0);
const fmtFecha = d  => d ? new Date(d+'T12:00').toLocaleDateString('es-CO',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const eColor   = e  => e==='confirmada'||e==='realizada'?'green':e==='pendiente'?'amber':e==='cancelada'||e==='no_asistio'?'red':'blue';
const pColor   = e  => e==='activo'?'green':e==='pausa'?'amber':e==='formulario_pendiente'?'amber':e==='inactivo'?'red':'blue';

// ── Componentes pequeños ──────────────────────────────────────
function Modal({title,onClose,children,color=C.teal,wide=false}){
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(45,79,74,0.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',zIndex:1000,overflowY:'auto'}}>
      <div style={{...card,width:'100%',maxWidth:wide?'760px':'560px',maxHeight:'93vh',display:'flex',flexDirection:'column'}}>
        <div style={{background:`linear-gradient(135deg,${color},${color}CC)`,color:'#fff',padding:'1.1rem 1.5rem',borderRadius:'16px 16px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <span style={{fontWeight:700,fontSize:'1rem'}}>{title}</span>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#fff',display:'flex'}}><X size={20}/></button>
        </div>
        <div style={{padding:'1.5rem',overflowY:'auto',flex:1}}>{children}</div>
      </div>
    </div>
  );
}
function F({label,children,col2=false}){
  return(
    <div style={{marginBottom:'0.85rem',...(col2&&{})}}>
      <label style={{display:'block',fontSize:'0.78rem',fontWeight:700,color:C.teal,marginBottom:'0.3rem',textTransform:'uppercase',letterSpacing:'0.04em'}}>{label}</label>
      {children}
    </div>
  );
}
function Kpi({label,value,Icon,color=C.ocre,sub}){
  return(
    <div style={{...card,padding:'0.7rem 0.85rem',display:'flex',alignItems:'center',gap:'0.6rem'}}>
      <div style={{width:34,height:34,borderRadius:'9px',background:color+'20',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Icon size={16} color={color}/></div>
      <div style={{minWidth:0}}>
        <div style={{fontSize:'0.68rem',color:C.gray,fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{label}</div>
        <div style={{fontSize:'1.1rem',fontWeight:700,color:C.teal,lineHeight:1.2}}>{value}</div>
        {sub&&<div style={{fontSize:'0.67rem',color:C.gray}}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Impresión ─────────────────────────────────────────────────

// ════════════════════════════════════════════════════════════
// Componente: Cumpleaños
// ════════════════════════════════════════════════════════════
function TabCumpleanos({apiFetch,notify,btn,inp,C,card,badge,googleStatus}){
  const [pacientes,setPacientes] = useState([]);
  const [buscar,setBuscar]       = useState('');
  const [soloProximos,setSoloProximos] = useState(false);
  const [sincronizando,setSincronizando] = useState(false);
  const [descartados,setDescartados] = useState([]);

  useEffect(()=>{
    apiFetch('/pacientes.php').then(d=>{ if(Array.isArray(d)) setPacientes(d); });
  },[]); // eslint-disable-line

  const hoy = new Date(); hoy.setHours(12,0,0,0);

  // Calcular inicio y fin de semana (lunes a domingo)
  const inicioSemana = new Date(hoy);
  const diaActual = hoy.getDay();
  const diasAlLunes = diaActual === 0 ? -6 : 1 - diaActual;
  inicioSemana.setDate(hoy.getDate() + diasAlLunes);
  inicioSemana.setHours(0,0,0,0);

  const finSemana = new Date(inicioSemana);
  finSemana.setDate(inicioSemana.getDate() + 6);
  finSemana.setHours(23,59,59,999);

  const conCumple = pacientes
    .filter(p => p.fecha_nacimiento)
    .map(p => {
      const fn = new Date(p.fecha_nacimiento + 'T00:00');
      let cumple = new Date(hoy.getFullYear(), fn.getMonth(), fn.getDate());
      cumple.setHours(0,0,0,0);
      if (cumple < hoy) cumple = new Date(hoy.getFullYear()+1, fn.getMonth(), fn.getDate());
      cumple.setHours(0,0,0,0);
      const hoyNorm = new Date(hoy);
      hoyNorm.setHours(0,0,0,0);
      const diffMs   = cumple - hoyNorm;
      const diasFalt = Math.floor(diffMs / 86400000);
      const edad     = cumple.getFullYear() - fn.getFullYear();

      // Cumpleaños de esta semana (incluso si ya pasó)
      const cumpleEstaSemanaPasada = new Date(hoy.getFullYear(), fn.getMonth(), fn.getDate());
      const esDeEstaSemana = cumpleEstaSemanaPasada >= inicioSemana && cumpleEstaSemanaPasada <= finSemana;

      return { ...p, cumpleFecha: cumple.toISOString().slice(0,10), diasFalt, edad, esDeEstaSemana };
    })
    .filter(p => {
      const texto = buscar.toLowerCase();
      const coincide = !buscar || `${p.nombre} ${p.apellido}`.toLowerCase().includes(texto);
      const proximo  = !soloProximos || p.diasFalt <= 30;
      const noDescartado = !descartados.includes(p.id);
      return coincide && proximo && noDescartado;
    })
    .sort((a,b) => {
      // Ordenar por urgencia: HOY > Mañana > próximos 7 días > resto
      const urgenciaA = a.diasFalt === 0 ? 0 : a.diasFalt === 1 ? 1 : a.diasFalt <= 7 ? 2 : 3;
      const urgenciaB = b.diasFalt === 0 ? 0 : b.diasFalt === 1 ? 1 : b.diasFalt <= 7 ? 2 : 3;
      if (urgenciaA !== urgenciaB) return urgenciaA - urgenciaB;
      return a.diasFalt - b.diasFalt;
    });

  const sinFecha = pacientes.filter(p => !p.fecha_nacimiento);

  const sincrGoogle = async () => {
    setSincronizando(true);
    const r = await apiFetch('/cumpleanos_sync.php',{method:'POST'});
    setSincronizando(false);
    if(r?.creados!==undefined) notify(`${r.creados} cumpleaños sincronizados a Google Calendar`);
    else notify(r?.error||'Error al sincronizar','error');
  };

  return (
    <div>
      {/* Cabecera */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.2rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <div>
          <h2 style={{fontSize:'1.25rem',fontWeight:700,color:C.teal,margin:0}}>Cumpleaños</h2>
          <p style={{fontSize:'0.82rem',color:C.gray,margin:0}}>Fechas de nacimiento de tus pacientes</p>
        </div>
        {googleStatus?.connected&&(
          <button style={btn('ghost')} onClick={sincrGoogle} disabled={sincronizando}>
            <Calendar size={14}/> {sincronizando?'Sincronizando...':'Sync Google Calendar'}
          </button>
        )}
      </div>

      {/* Alertas de hoy/mañana/pasado */}
      {conCumple.filter(p=>p.diasFalt<=2).map(p=>(
        <div key={p.id} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.8rem 1.1rem',borderRadius:'12px',marginBottom:'0.6rem',background:p.diasFalt===0?'#FFF7ED':'#FFFBEB',border:`1.5px solid ${p.diasFalt===0?'#F59E0B':'#FDE68A'}`}}>
          <span style={{fontSize:'1.5rem'}}>🎂</span>
          <div style={{flex:1}}>
            <span style={{fontWeight:700,color:C.teal}}>{p.nombre} {p.apellido}</span>
            <span style={{fontSize:'0.8rem',color:C.gray,marginLeft:'0.5rem'}}>cumple {p.edad} años</span>
          </div>
          <span style={{fontWeight:700,color:'#92400E',fontSize:'0.82rem'}}>
            {p.diasFalt===0?'¡HOY! 🎉':p.diasFalt===1?'Mañana':'En 2 días'}
          </span>
          {p.whatsapp&&(
            <a href={`https://wa.me/57${(p.whatsapp||'').replace(/^\+/,'').replace(/^57/,'')}?text=Hola%20${p.nombre},%20¡feliz%20cumpleaños!%20🎉%20Te%20deseo%20un%20día%20muy%20especial.`}
              target="_blank" rel="noopener noreferrer"
              style={{...btn('whatsapp',true),textDecoration:'none'}}>
              <MessageCircle size={12}/> WhatsApp
            </a>
          )}
        </div>
      ))}

      {/* Filtros */}
      <div style={{display:'flex',gap:'0.75rem',marginBottom:'1rem',flexWrap:'wrap',alignItems:'center'}}>
        <div style={{flex:'1 1 220px',position:'relative'}}>
          <Search size={15} style={{position:'absolute',left:'0.7rem',top:'50%',transform:'translateY(-50%)',color:C.gray,pointerEvents:'none'}}/>
          <input style={{...inp,paddingLeft:'2.1rem'}} placeholder="Buscar paciente..." value={buscar} onChange={e=>setBuscar(e.target.value)}/>
        </div>
        <label style={{display:'flex',alignItems:'center',gap:'0.4rem',fontSize:'0.83rem',color:C.teal,cursor:'pointer',userSelect:'none'}}>
          <input type="checkbox" checked={soloProximos} onChange={e=>setSoloProximos(e.target.checked)} style={{accentColor:C.ocre}}/>
          Solo próximos 30 días
        </label>
        <span style={{fontSize:'0.82rem',color:C.gray,marginLeft:'auto'}}>{conCumple.length} paciente(s)</span>
      </div>

      {/* Cards de cumpleaños */}
      <div style={{marginBottom:'1.5rem'}}>
        {conCumple.length===0?(
          <div style={{...card,padding:'3rem',textAlign:'center',color:C.gray}}>
            <Cake size={40} style={{margin:'0 auto 0.7rem',opacity:0.2,display:'block'}}/>
            <p style={{fontWeight:600}}>Sin resultados</p>
          </div>
        ):(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:'1rem'}}>
            {conCumple.map(p=>{
              const urgencia = p.diasFalt===0?'today':p.diasFalt===1?'soon':p.diasFalt<=7?'week':'normal';
              const bgCard = urgencia==='today'?'linear-gradient(135deg,#FEF3C7,#FCD34D)':urgencia==='soon'?'linear-gradient(135deg,#FECACA,#FBBF24)':urgencia==='week'?'linear-gradient(135deg,#F3E8FF,#E9D5FF)':'linear-gradient(135deg,#F9FAFB,#F3F4F6)';
              const borderColor = urgencia==='today'?'#FBBF24':urgencia==='soon'?'#FB923C':urgencia==='week'?C.ocre:C.border;
              const iconColor = urgencia==='today'?'#D97706':urgencia==='soon'?'#D97706':urgencia==='week'?C.ocre:C.gray;

              return (
                <div key={p.id} style={{background:bgCard,borderRadius:14,border:`2px solid ${borderColor}`,padding:'1rem 1.2rem',boxShadow:'0 4px 12px rgba(45,79,74,0.08)',transition:'all 0.3s ease',cursor:'pointer',position:'relative',overflow:'hidden'}}>
                  {/* Indicator bar */}
                  <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:borderColor}}/>

                  {/* Header con nombre y edad */}
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'0.8rem',marginTop:'0.2rem'}}>
                    <div>
                      <div style={{fontWeight:700,color:C.teal,fontSize:'0.95rem'}}>{p.nombre} {p.apellido}</div>
                      <div style={{fontSize:'0.78rem',color:C.gray,marginTop:'0.15rem'}}>📅 Nac. {p.fecha_nacimiento?.split('-').reverse().join('/')}</div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',width:'2.5rem',height:'2.5rem',background:C.white,borderRadius:'50%',fontWeight:700,color:C.teal,fontSize:'0.9rem',boxShadow:'0 2px 8px rgba(45,79,74,0.15)'}}>
                      {p.edad}
                    </div>
                  </div>

                  {/* Dates */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.6rem',marginBottom:'0.8rem',fontSize:'0.78rem'}}>
                    <div>
                      <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',marginBottom:'0.15rem'}}>Próximo</div>
                      <div style={{color:C.gray,fontWeight:600}}>{p.cumpleFecha.split('-').reverse().join('/')}</div>
                    </div>
                    <div>
                      <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',marginBottom:'0.15rem'}}>Faltan</div>
                      <div style={{fontWeight:700,color:iconColor,fontSize:'0.9rem'}}>
                        {p.diasFalt===0?'¡HOY! 🎉':p.diasFalt===1?'Mañana 🎂':`${p.diasFalt} días`}
                      </div>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div style={{display:'flex',gap:'0.5rem',paddingTop:'0.8rem',borderTop:`1px solid ${borderColor}33`}}>
                    {p.whatsapp&&(
                      <a href={`https://wa.me/57${(p.whatsapp||'').replace(/^\+/,'').replace(/^57/,'')}?text=Hola%20${p.nombre},%20¡feliz%20cumpleaños!%20🎉`}
                        target="_blank" rel="noopener noreferrer"
                        style={{...btn('whatsapp',true),textDecoration:'none',fontSize:'0.75rem',padding:'0.4rem 0.8rem',flex:1,justifyContent:'center'}}>
                        <MessageCircle size={13}/> WhatsApp
                      </a>
                    )}
                    <button onClick={()=>setDescartados([...descartados,p.id])}
                      style={{...btn('ghost'),fontSize:'0.75rem',padding:'0.4rem 0.8rem',border:`1.5px solid ${borderColor}`}} title="Descartar">
                      <CheckCircle size={13}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pacientes sin fecha */}
      {sinFecha.length>0&&(
        <div style={{fontSize:'0.8rem',color:C.gray,padding:'0.6rem 1rem',background:C.crema,borderRadius:'10px',border:`1px solid ${C.border}`}}>
          <strong>{sinFecha.length} paciente(s)</strong> sin fecha de nacimiento registrada: {sinFecha.map(p=>`${p.nombre} ${p.apellido}`).join(', ')}
        </div>
      )}
    </div>
  );
}

// ── Diagnósticos usados en consulta ──────────────────────────


function DSM5Picker({value, onChange, inp, C}){
  const [buscar, setBuscar] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const ref = useRef();

  // value es string separado por " | "
  const seleccionados = value ? value.split(' | ').filter(d => d && d !== 'dummy_end') : [];

  // Cargar diagnósticos de la API
  useEffect(() => {
    const cargarDiag = async () => {
      try {
        console.log('Cargando diagnósticos desde:', `${API_BASE}/diagnosticos.php`);
        const res = await fetch(`${API_BASE}/diagnosticos.php`);
        console.log('Respuesta status:', res.status);
        const data = await res.json();
        console.log('Datos recibidos:', data);
        setDiagnosticos(data || []);
      } catch (err) {
        console.error('Error cargando diagnósticos:', err);
        setDiagnosticos([]);
      } finally {
        setCargando(false);
      }
    };
    cargarDiag();
  }, []);

  const agregar = (diag) => {
    const label = `${diag.CODIGO} - ${diag.DESCRIPCION}`;
    if(seleccionados.includes(label)) return;
    onChange([...seleccionados, label].join(' | '));
    setBuscar('');
    setAbierto(false);
  };

  const quitar = (diag) => {
    onChange(seleccionados.filter(s=>s!==diag).join(' | '));
  };

  const filtrados = buscar.length >= 1
    ? diagnosticos.filter(d =>
        d.CODIGO.toLowerCase().includes(buscar.toLowerCase()) ||
        d.DESCRIPCION.toLowerCase().includes(buscar.toLowerCase())
      )
    : diagnosticos;

  // Cierra el dropdown al hacer click fuera
  useEffect(()=>{
    const handler = e => { if(ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', handler);
    return ()=>document.removeEventListener('mousedown', handler);
  },[]);

  const [posicion, setPosicion] = useState({top:0, left:0, width:0});

  const inputRef = useRef();
  useEffect(() => {
    if(inputRef.current && abierto) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosicion({top: rect.bottom + 4, left: rect.left, width: rect.width});
    }
  }, [abierto]);

  return (
    <div ref={ref}>
      {/* Chips de diagnósticos seleccionados */}
      {seleccionados.length>0&&(
        <div style={{display:'flex',flexWrap:'wrap',gap:'0.3rem',marginBottom:'0.5rem'}}>
          {seleccionados.map(s=>(
            <span key={s} style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',background:'#EDE9FE',color:'#5B21B6',border:'1px solid #C4B5FD',borderRadius:20,padding:'0.2rem 0.55rem',fontSize:'0.75rem',fontWeight:600}}>
              {s}
              <button onClick={()=>quitar(s)} style={{background:'none',border:'none',cursor:'pointer',color:'#7C3AED',padding:0,display:'flex',lineHeight:1}}>
                <X size={11}/>
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Input de búsqueda */}
      <div style={{position:'relative'}}>
        <Search size={13} style={{position:'absolute',left:'0.65rem',top:'50%',transform:'translateY(-50%)',color:C.gray,pointerEvents:'none'}}/>
        <input
          ref={inputRef}
          style={{...inp,paddingLeft:'2rem',fontSize:'0.83rem'}}
          placeholder="Buscar o seleccionar diagnóstico..."
          value={buscar}
          onChange={e=>{setBuscar(e.target.value);setAbierto(true);}}
          onFocus={()=>setAbierto(true)}
        />
      </div>
      {/* Dropdown - position:fixed */}
      {abierto&&filtrados.length>0&&(
        <div style={{position:'fixed',top:posicion.top,left:posicion.left,width:posicion.width,background:'#fff',border:`1.5px solid ${C.border}`,borderRadius:10,boxShadow:'0 4px 16px rgba(45,79,74,0.12)',zIndex:9999,overflow:'hidden',maxHeight:220,overflowY:'auto'}}>
          {filtrados.map(d=>{
            const label = `${d.CODIGO} - ${d.DESCRIPCION}`;
            return (
              <div key={d.CODIGO} onClick={()=>{
                if(!seleccionados.includes(label)) {
                  onChange([...seleccionados, label].join(' | '));
                }
                setBuscar('');
                setAbierto(false);
              }}
                style={{padding:'0.45rem 0.85rem',cursor:'pointer',fontSize:'0.83rem',color:C.teal,borderBottom:`1px solid ${C.border}`,background:seleccionados.includes(label)?'#F5F3FF':'',transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.background='#F5F3FF'}
                onMouseLeave={e=>e.currentTarget.style.background=seleccionados.includes(label)?'#F5F3FF':''}>
                <div style={{fontWeight:600}}>{d.CODIGO}</div>
                <div style={{fontSize:'0.75rem',color:C.gray}}>{d.DESCRIPCION}</div>
              </div>
            );
          })}
        </div>
      )}
      {abierto&&buscar.length>=1&&filtrados.length===0&&(
        <div style={{position:'fixed',top:posicion.top,left:posicion.left,width:posicion.width,background:'#fff',border:`1.5px solid ${C.border}`,borderRadius:10,padding:'0.65rem',fontSize:'0.82rem',color:C.gray,zIndex:9999}}>
          Sin resultados.
        </div>
      )}
    </div>
  );
}

// Colores pastel por mes
const MES_COLORS = [
  {bg:'#FDE8E8',txt:'#B91C1C'}, // Ene
  {bg:'#FEE2D5',txt:'#C2410C'}, // Feb
  {bg:'#FEF3C7',txt:'#B45309'}, // Mar
  {bg:'#D1FAE5',txt:'#065F46'}, // Abr
  {bg:'#CFFAFE',txt:'#0E7490'}, // May
  {bg:'#E0F2FE',txt:'#075985'}, // Jun
  {bg:'#EDE9FE',txt:'#5B21B6'}, // Jul
  {bg:'#FCE7F3',txt:'#9D174D'}, // Ago
  {bg:'#FDF4FF',txt:'#7E22CE'}, // Sep
  {bg:'#FEF9C3',txt:'#92400E'}, // Oct
  {bg:'#ECFDF5',txt:'#065F46'}, // Nov
  {bg:'#F1F5F9',txt:'#334155'}, // Dic
];

function CitaRow({c,btn,C,MESES,apiFetch,notify,setCitas,setCitaEditando,setFCita,setMEditCita,eliminarCita,cambiarEstado,cargarDetPac,setSelPac,setMHistoria}){
  const mes = parseInt(c.fecha?.slice(5,7))-1;
  const mc  = MES_COLORS[mes] || MES_COLORS[0];
  const ESTADOS_SEL = ['pendiente','confirmada','realizada','cancelada','no_asistio'];
  const [estado, setEstado] = useState(c.estado);

  const onEstadoChange = async e => {
    const v = e.target.value;
    const prev = estado;
    setEstado(v);
    if(setCitas) setCitas(old=>old.map(x=>x.id===c.id?{...x,estado:v}:x));
    const res = await apiFetch(`/citas.php?id=${c.id}`,{method:'PUT',body:JSON.stringify({estado:v})});
    if(res && res.mensaje) {
      notify('Estado actualizado');
    } else {
      // revertir si falló
      setEstado(prev);
      if(setCitas) setCitas(old=>old.map(x=>x.id===c.id?{...x,estado:prev}:x));
      notify('Error al guardar estado','error');
    }
  };

  return (
    <div style={{padding:'0.65rem 1.1rem',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:'0.7rem',flexWrap:'wrap'}}>
      {/* Caja fecha */}
      <div style={{textAlign:'center',minWidth:38,background:mc.bg,borderRadius:8,padding:'0.18rem 0.38rem',flexShrink:0}}>
        <div style={{fontSize:'0.95rem',fontWeight:700,color:mc.txt,lineHeight:1.1}}>{c.fecha?.slice(8)}</div>
        <div style={{fontSize:'0.55rem',color:mc.txt,textTransform:'uppercase',opacity:0.85}}>{MESES[mes]?.slice(0,3)}</div>
      </div>
      {/* Info */}
      <div style={{flex:'1 1 160px',minWidth:0}}>
        <div style={{fontWeight:700,color:C.teal,fontSize:'0.86rem',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{c.paciente_nombre}</div>
        <div style={{fontSize:'0.72rem',color:C.gray,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
          {c.hora?.slice(0,5)} · {c.duracion}min · {c.tipo?.replace(/_/g,' ')} · {c.modalidad?.replace(/_/g,' ')}
        </div>
      </div>
      {/* Selector estado */}
      <select value={estado} onChange={onEstadoChange}
        style={{padding:'0.2rem 0.4rem',borderRadius:'8px',border:`1.5px solid ${C.border}`,fontSize:'0.72rem',fontWeight:600,color:C.teal,background:C.crema,cursor:'pointer',fontFamily:"'MyriadPro',Arial,sans-serif",flexShrink:0}}>
        {ESTADOS_SEL.map(e=><option key={e} value={e}>{e.replace('_',' ')}</option>)}
      </select>
      {/* Botones: Perfil · Meet · WhatsApp · Editar · Eliminar */}
      <div style={{display:'flex',gap:'0.3rem',flexShrink:0,alignItems:'center'}}>
        {cargarDetPac&&setSelPac&&setMHistoria&&(
          <button title="Ver perfil del paciente"
            style={{...btn('ghost',true),padding:'0.28rem 0.5rem',background:C.tealLight,border:'none',color:C.teal}}
            onClick={async()=>{ await cargarDetPac(c.paciente_id); setSelPac({id:c.paciente_id,nombre:c.paciente_nombre}); setMHistoria(true); }}>
            <User size={12}/>
          </button>
        )}
        {c.modalidad==='virtual'&&c.meet_link
          ? <a href={c.meet_link} target="_blank" rel="noopener noreferrer" style={{...btn('primary',true),background:'#1a73e8',textDecoration:'none',padding:'0.28rem 0.5rem'}}><Video size={12}/></a>
          : <span style={{width:30,display:'inline-block'}}/>
        }
        {c.whatsapp
          ? <a href={`https://wa.me/57${(c.whatsapp||'').replace(/^\+/,'').replace(/^57/,'')}`} target="_blank" rel="noopener noreferrer" style={{...btn('whatsapp',true),textDecoration:'none',padding:'0.28rem 0.5rem'}}><MessageCircle size={12}/></a>
          : <span style={{width:30,display:'inline-block'}}/>
        }
        <button style={{...btn('ghost',true),padding:'0.28rem 0.5rem'}} onClick={()=>{ setCitaEditando(c); setFCita({paciente_id:c.paciente_id,fecha:c.fecha,hora:c.hora?.slice(0,5),duracion:c.duracion,tipo:c.tipo,modalidad:c.modalidad||'presencial_consultorio',estado,notas:c.notas||'',meet_link:c.meet_link||''}); setMEditCita(true); }}><Edit2 size={12}/></button>
        <button style={{...btn('danger',true),padding:'0.28rem 0.5rem'}} onClick={()=>eliminarCita(c)}><Trash2 size={12}/></button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Componente: Todas las Citas
// ════════════════════════════════════════════════════════════
function TabCitas({apiFetch,notify,btn,inp,C,card,badge,eColor,cambiarEstado,setCitaEditando,setFCita,setMEditCita,eliminarCita,setMCita,hoy,cargarDetPac,setSelPac,setMHistoria}){
  const [citas,setCitas]   = useState([]);
  const [loading,setLoading] = useState(true);
  const [buscar,setBuscar]   = useState('');
  const [filtroEstado,setFiltroEstado] = useState('pendiente');
  const [filtroMes,setFiltroMes]       = useState('');

  useEffect(()=>{
    apiFetch('/citas.php').then(d=>{ setCitas(Array.isArray(d)?d:[]); setLoading(false); }).catch(()=>setLoading(false));
  },[]); // eslint-disable-line

  const citasFiltradas = citas.filter(c=>{
    const texto = buscar.toLowerCase();
    const coincideTexto = !buscar ||
      c.paciente_nombre?.toLowerCase().includes(texto) ||
      c.tipo?.toLowerCase().includes(texto) ||
      c.notas?.toLowerCase().includes(texto) ||
      c.fecha?.includes(texto);
    const coincideEstado = !filtroEstado || c.estado === filtroEstado;
    const coincideMes    = !filtroMes    || c.fecha?.slice(0,7) === filtroMes;
    return coincideTexto && coincideEstado && coincideMes;
  });

  const FILTROS = [
    {v:'pendiente',  l:'Pendiente'},
    {v:'confirmada', l:'Confirmada'},
    {v:'cancelada',  l:'Cancelada'},
    {v:'no_asistio', l:'No asistió'},
    {v:'realizada',  l:'Realizada'},
  ];

  const conteo = v => citas.filter(c=>c.estado===v).length;

  return (
    <div>
      {/* Fila superior: búsqueda + Nueva cita */}
      <div style={{display:'flex',gap:'0.75rem',marginBottom:'0.75rem',alignItems:'center'}}>
        <div style={{flex:1,position:'relative'}}>
          <Search size={15} style={{position:'absolute',left:'0.7rem',top:'50%',transform:'translateY(-50%)',color:C.gray,pointerEvents:'none'}}/>
          <input style={{...inp,paddingLeft:'2.1rem'}} placeholder="Buscar paciente, tipo, fecha..." value={buscar} onChange={e=>setBuscar(e.target.value)}/>
        </div>
        <button style={btn('primary')} onClick={()=>{ setFCita(f=>({...f,fecha:hoy})); setMCita(true); }}>
          <Plus size={15}/> Nueva cita
        </button>
      </div>

      {/* Chips de estado + filtro mes + limpiar */}
      <div style={{display:'flex',gap:'0.4rem',marginBottom:'1rem',flexWrap:'wrap',alignItems:'center'}}>
        <button onClick={()=>setFiltroEstado('')}
          style={{padding:'0.3rem 0.85rem',borderRadius:'20px',border:`1.5px solid ${!filtroEstado?C.teal:C.border}`,background:!filtroEstado?C.teal:'transparent',color:!filtroEstado?'#fff':C.gray,fontSize:'0.78rem',fontWeight:600,cursor:'pointer',fontFamily:"'MyriadPro',Arial,sans-serif"}}>
          Todos <span style={{opacity:0.7}}>({citas.length})</span>
        </button>
        {FILTROS.map(f=>{
          const active = filtroEstado===f.v;
          const n = conteo(f.v);
          return (
            <button key={f.v} onClick={()=>setFiltroEstado(f.v)}
              style={{padding:'0.3rem 0.85rem',borderRadius:'20px',border:`1.5px solid ${active?C.teal:C.border}`,background:active?C.teal:'transparent',color:active?'#fff':C.gray,fontSize:'0.78rem',fontWeight:600,cursor:'pointer',fontFamily:"'MyriadPro',Arial,sans-serif"}}>
              {f.l} <span style={{opacity:0.7}}>({n})</span>
            </button>
          );
        })}
        <input type="month" style={{...inp,width:'auto',minWidth:'130px',padding:'0.28rem 0.6rem',fontSize:'0.78rem'}} value={filtroMes} onChange={e=>setFiltroMes(e.target.value)}/>
        {(buscar||filtroMes)&&<button style={btn('ghost',true)} onClick={()=>{setBuscar('');setFiltroMes('');}}><X size={13}/> Limpiar</button>}
        <span style={{fontSize:'0.82rem',color:C.gray,marginLeft:'auto',alignSelf:'center'}}>{citasFiltradas.length} cita(s)</span>
      </div>

      {/* Lista */}
      <div style={{...card,overflow:'hidden'}}>
        {loading?(
          <div style={{padding:'3rem',textAlign:'center',color:C.gray}}>Cargando...</div>
        ):citasFiltradas.length===0?(
          <div style={{padding:'3rem',textAlign:'center',color:C.gray}}>
            <Calendar size={40} style={{margin:'0 auto 0.7rem',opacity:0.2,display:'block'}}/>
            <p style={{fontWeight:600}}>Sin citas que coincidan</p>
          </div>
        ):citasFiltradas.map(c=>(
          <CitaRow key={c.id} c={c} btn={btn} C={C} MESES={['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']}
            apiFetch={apiFetch} notify={notify} setCitas={setCitas}
            setCitaEditando={setCitaEditando} setFCita={setFCita} setMEditCita={setMEditCita} eliminarCita={eliminarCita}
            cargarDetPac={cargarDetPac} setSelPac={setSelPac} setMHistoria={setMHistoria}/>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Componente: Gestión de Usuarios (solo admin)
// ════════════════════════════════════════════════════════════
function TabUsuarios({apiFetch,notify,btn,inp,F,C,card}){
  const [usuarios,setUsuarios] = useState([]);
  const [mForm,setMForm]       = useState(false);
  const [editando,setEditando] = useState(null); // null=nuevo, obj=editar
  const [form,setForm]         = useState({nombre:'',email:'',password:'',rol:'psicologa'});

  const cargar = useCallback(async()=>{
    const d = await apiFetch('/usuarios.php');
    if(Array.isArray(d)) setUsuarios(d);
  },[apiFetch]);

  useEffect(()=>{ cargar(); },[cargar]);

  const abrirNuevo = ()=>{ setForm({nombre:'',email:'',password:'',rol:'psicologa'}); setEditando(null); setMForm(true); };
  const abrirEditar = u=>{ setForm({nombre:u.nombre,email:u.email,password:'',rol:u.rol}); setEditando(u); setMForm(true); };

  const guardar = async e=>{
    e.preventDefault();
    if(!editando && form.password.length<8){ notify('La contraseña debe tener al menos 8 caracteres','error'); return; }
    const body = editando
      ? {nombre:form.nombre,email:form.email,rol:form.rol,...(form.password?{password:form.password}:{})}
      : {nombre:form.nombre,email:form.email,password:form.password,rol:form.rol};
    const r = editando
      ? await apiFetch(`/usuarios.php?id=${editando.id}`,{method:'PUT',body:JSON.stringify(body)})
      : await apiFetch('/usuarios.php',{method:'POST',body:JSON.stringify(body)});
    if(r?.id||r?.mensaje){ notify(editando?'Usuario actualizado':'Usuario creado'); setMForm(false); cargar(); }
    else notify(r?.error||'Error al guardar','error');
  };

  const eliminar = async u=>{
    if(!window.confirm(`¿Eliminar el usuario "${u.nombre}"? Esta acción no se puede deshacer.`)) return;
    const r = await apiFetch(`/usuarios.php?id=${u.id}`,{method:'DELETE'});
    if(r?.mensaje){ notify('Usuario eliminado'); cargar(); }
    else notify(r?.error||'Error al eliminar','error');
  };

  return(
    <div style={{maxWidth:700,margin:'0 auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.2rem'}}>
        <h2 style={{fontWeight:700,color:C.teal,fontSize:'1.1rem',display:'flex',alignItems:'center',gap:'0.5rem'}}><Shield size={16}/> Gestión de usuarios</h2>
        <button style={btn('primary')} onClick={abrirNuevo}><UserPlus size={14}/> Nuevo usuario</button>
      </div>

      <div style={{...card,overflow:'hidden'}}>
        {usuarios.length===0?(
          <div style={{padding:'2rem',textAlign:'center',color:C.gray}}>
            <Shield size={36} style={{margin:'0 auto 0.5rem',opacity:0.2,display:'block'}}/>
            <p>No hay usuarios registrados</p>
          </div>
        ):usuarios.map((u,i)=>(
          <div key={u.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.85rem 1.2rem',borderTop:i>0?`1px solid ${C.border}`:'none'}}>
            <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
              <div style={{width:38,height:38,borderRadius:'50%',background:C.ocreLight,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,color:C.ocre,fontSize:'1rem'}}>
                {u.nombre?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{fontWeight:600,color:C.teal,fontSize:'0.88rem'}}>{u.nombre}</div>
                <div style={{fontSize:'0.75rem',color:C.gray}}>{u.email}</div>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
              <span style={{fontSize:'0.72rem',fontWeight:600,padding:'0.2rem 0.6rem',borderRadius:6,background:u.rol==='admin'?'#FEE2E2':C.tealLight,color:u.rol==='admin'?'#DC2626':C.teal,textTransform:'uppercase'}}>
                {u.rol}
              </span>
              <span style={{fontSize:'0.72rem',padding:'0.2rem 0.6rem',borderRadius:6,background:u.activo?'#D1FAE5':'#F3F4F6',color:u.activo?'#16a34a':C.gray}}>
                {u.activo?'Activo':'Inactivo'}
              </span>
              <button style={btn('ghost',true)} onClick={()=>abrirEditar(u)}><Edit2 size={13}/></button>
              <button style={btn('danger',true)} onClick={()=>eliminar(u)}><Trash2 size={13}/></button>
            </div>
          </div>
        ))}
      </div>

      {mForm&&(
        <div style={{position:'fixed',inset:0,background:'rgba(45,79,74,0.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',zIndex:1000}}>
          <div style={{background:C.white,borderRadius:16,width:'100%',maxWidth:440,boxShadow:'0 20px 60px rgba(0,0,0,0.18)'}}>
            <div style={{background:C.teal,color:'#fff',padding:'1rem 1.5rem',borderRadius:'16px 16px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontWeight:700}}>{editando?'Editar usuario':'Nuevo usuario'}</span>
              <button onClick={()=>setMForm(false)} style={{background:'none',border:'none',color:'#fff',cursor:'pointer',fontSize:'1.2rem'}}>×</button>
            </div>
            <form onSubmit={guardar} style={{padding:'1.5rem'}}>
              <F label="Nombre *"><input style={inp} value={form.nombre} onChange={e=>setForm({...form,nombre:e.target.value})} required/></F>
              <F label="Email *"><input style={inp} type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required/></F>
              <F label={editando?"Nueva contraseña (dejar vacío para no cambiar)":"Contraseña * (mín. 8 caracteres)"}>
                <input style={inp} type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} {...(!editando?{required:true}:{})} placeholder="••••••••"/>
              </F>
              <F label="Rol">
                <select style={inp} value={form.rol} onChange={e=>setForm({...form,rol:e.target.value})}>
                  <option value="psicologa">Psicóloga</option>
                  <option value="admin">Administrador</option>
                </select>
              </F>
              <div style={{display:'flex',gap:'0.75rem',marginTop:'1rem'}}>
                <button type="button" onClick={()=>setMForm(false)} style={btn('ghost')}>Cancelar</button>
                <button type="submit" style={{...btn('primary'),flex:1}}>{editando?'Guardar cambios':'Crear usuario'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Componente: Mi Perfil
// ════════════════════════════════════════════════════════════
function TabPerfil({apiFetch,apiUpload,notify,updateUser,btn,inp,C,card,googleStatus,connectGoogle,disconnectGoogle,apiBase}){
  const [perfil, setPerfil] = useState(null);

  useEffect(()=>{
    apiFetch('/perfil.php').then(d=>{ if(d?.id) setPerfil(d); });
  },[]); // eslint-disable-line

  if(!perfil) return <div style={{textAlign:'center',padding:'3rem',color:C.gray}}>Cargando...</div>;

  const guardarDatos = async e => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const r = await apiFetch('/perfil.php',{method:'PUT',body:JSON.stringify({
      nombre: fd.get('nombre'),
      email:  fd.get('email'),
    })});
    if(r?.user){ updateUser(r.user); setPerfil(p=>({...p,...r.user})); notify('Perfil actualizado'); }
    else notify(r?.error||'Error al guardar','error');
  };

  const guardarProfesional = async e => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const r = await apiFetch('/perfil.php',{method:'PUT',body:JSON.stringify({
      codigo_retus:        fd.get('codigo_retus'),
      tarjeta_profesional: fd.get('tarjeta_profesional'),
    })});
    if(r?.user){ updateUser(r.user); setPerfil(p=>({...p,...r.user})); notify('Datos guardados'); }
    else notify(r?.error||'Error al guardar','error');
  };

  const guardarPassword = async e => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if(fd.get('nueva')!==fd.get('confirmar')){ notify('Las contraseñas no coinciden','error'); return; }
    if(fd.get('nueva').length<8){ notify('Mínimo 8 caracteres','error'); return; }
    const r = await apiFetch('/perfil.php',{method:'PUT',body:JSON.stringify({
      accion:'cambiar_password',
      password_actual: fd.get('actual'),
      password_nuevo:  fd.get('nueva'),
    })});
    if(r?.mensaje){ notify('Contraseña actualizada'); e.target.reset(); }
    else notify(r?.error||'Error al cambiar contraseña','error');
  };

  const subirFirma = async e => {
    const file = e.target.files[0];
    if(!file) return;
    const fd = new FormData();
    fd.append('firma', file);
    const data = await apiUpload('/perfil.php', fd);
    if(data?.firma_url){
      updateUser({firma_url: data.firma_url});
      setPerfil(p=>({...p, firma_url: data.firma_url}));
      notify('Firma actualizada');
    } else notify(data?.error||'Error al subir firma','error');
    e.target.value='';
  };

  const firmaUrl = perfil.firma_url ? `${apiBase}${perfil.firma_url}` : null;

  return (
    <div style={{maxWidth:600,margin:'0 auto'}}>
      <h2 style={{fontWeight:700,color:C.teal,marginBottom:'1.5rem',fontSize:'1.1rem'}}>Mi Perfil</h2>

      {/* Datos personales */}
      <div style={{...card,padding:'1.5rem',marginBottom:'1.2rem'}}>
        <h3 style={{fontWeight:700,color:C.teal,marginBottom:'1rem',fontSize:'0.92rem',display:'flex',alignItems:'center',gap:'0.4rem'}}><User size={15}/> Datos personales</h3>
        <form onSubmit={guardarDatos}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <F label="Nombre"><input style={inp} name="nombre" defaultValue={perfil.nombre} required/></F>
            <F label="Email"><input style={inp} name="email" type="email" defaultValue={perfil.email} required/></F>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:'1rem'}}>
            <button type="submit" style={btn('primary',true)}><Edit2 size={13}/> Guardar cambios</button>
          </div>
        </form>
      </div>

      {/* Documentos profesionales */}
      <div style={{...card,padding:'1.5rem',marginBottom:'1.2rem'}}>
        <h3 style={{fontWeight:700,color:C.teal,marginBottom:'1rem',fontSize:'0.92rem',display:'flex',alignItems:'center',gap:'0.4rem'}}><FileText size={15}/> Información profesional</h3>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1.2rem',alignItems:'start'}}>
          {/* Firma */}
          <div>
            <div style={{fontSize:'0.72rem',fontWeight:700,color:C.gray,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:'0.5rem'}}>Firma (PNG/JPG)</div>
            {firmaUrl
              ? <img src={firmaUrl} alt="Firma" style={{maxHeight:70,maxWidth:'100%',border:`1px solid ${C.border}`,borderRadius:8,padding:'0.4rem',background:'#fff',display:'block',marginBottom:'0.5rem'}}/>
              : <p style={{fontSize:'0.8rem',color:C.gray,fontStyle:'italic',marginBottom:'0.5rem'}}>Sin firma cargada</p>
            }
            <label style={{display:'inline-flex',alignItems:'center',gap:'0.4rem',cursor:'pointer',fontSize:'0.78rem',color:C.teal,fontWeight:600,border:`1px solid ${C.border}`,borderRadius:8,padding:'0.35rem 0.8rem',background:C.crema}}>
              <Upload size={12}/> {firmaUrl ? 'Cambiar firma' : 'Subir firma'}
              <input type="file" accept="image/png,image/jpeg" style={{display:'none'}} onChange={subirFirma}/>
            </label>
          </div>
          {/* RETUS y tarjeta */}
          <form onSubmit={guardarProfesional}>
            <F label="Código RETUS">
              <input style={inp} name="codigo_retus" defaultValue={perfil.codigo_retus||''} placeholder="Ej: 05-6200-22"/>
            </F>
            <F label="Tarjeta profesional">
              <input style={{...inp,marginTop:'0.5rem'}} name="tarjeta_profesional" defaultValue={perfil.tarjeta_profesional||''} placeholder="Nº tarjeta profesional"/>
            </F>
            <div style={{marginTop:'0.75rem'}}>
              <button type="submit" style={btn('primary',true)}><Edit2 size={12}/> Guardar</button>
            </div>
          </form>
        </div>
      </div>

      {/* Seguridad */}
      <div style={{...card,padding:'1.5rem',marginBottom:'1.2rem'}}>
        <h3 style={{fontWeight:700,color:C.teal,marginBottom:'1rem',fontSize:'0.92rem',display:'flex',alignItems:'center',gap:'0.4rem'}}><KeyRound size={15}/> Seguridad</h3>
        <form onSubmit={guardarPassword}>
          <F label="Contraseña actual"><input style={inp} name="actual" type="password" required placeholder="••••••••"/></F>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem',marginTop:'0.75rem'}}>
            <F label="Nueva contraseña"><input style={inp} name="nueva" type="password" required placeholder="Mínimo 8 caracteres"/></F>
            <F label="Confirmar contraseña"><input style={inp} name="confirmar" type="password" required placeholder="Repite la contraseña"/></F>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:'1rem'}}>
            <button type="submit" style={btn('primary',true)}><KeyRound size={13}/> Cambiar contraseña</button>
          </div>
        </form>
      </div>

      {/* Google Calendar */}
      <div style={{...card,padding:'1.5rem'}}>
        <h3 style={{fontWeight:700,color:C.teal,marginBottom:'1rem',fontSize:'0.92rem',display:'flex',alignItems:'center',gap:'0.4rem'}}><Calendar size={15}/> Conexiones</h3>
        {googleStatus?.connected ? (
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:C.crema,borderRadius:10,padding:'0.85rem 1rem',border:`1px solid ${C.border}`}}>
            <div>
              <div style={{fontWeight:600,color:C.teal,fontSize:'0.87rem',display:'flex',alignItems:'center',gap:'0.4rem'}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'#16a34a',display:'inline-block'}}/>
                Google Calendar conectado
              </div>
              {googleStatus.google_email&&<div style={{fontSize:'0.75rem',color:C.gray,marginTop:'0.15rem'}}>{googleStatus.google_email}</div>}
            </div>
            <button style={btn('danger',true)} onClick={disconnectGoogle}>Desconectar</button>
          </div>
        ):(
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:C.crema,borderRadius:10,padding:'0.85rem 1rem',border:`1px solid ${C.border}`}}>
            <div style={{fontSize:'0.87rem',color:C.gray}}>Google Calendar no conectado</div>
            <button style={{...btn('ghost',true),borderColor:'#4285F4',color:'#4285F4'}} onClick={connectGoogle}><Calendar size={13}/> Conectar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function Dashboard(){
  const {user,logout,updateUser,apiFetch,apiUpload,token} = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(()=>{
    const handler = ()=>setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return ()=>window.removeEventListener('resize', handler);
  },[]);

  const [tab,setTab]     = useState('agenda');
  const [subTab,setSubTab] = useState('ingresos');
  const [mes,setMes]     = useState(new Date());
  const [search,setSearch] = useState('');
  const [verCal,setVerCal] = useState(false);
  const [filtroPac,setFiltroPac] = useState(''); // toggle calendario en Agenda

  // Datos
  const [citas,setCitas]         = useState([]);
  const [pacientes,setPacientes] = useState([]);
  const [gastos,setGastos]       = useState([]);
  const [kpis,setKpis]           = useState(null);
  const [pagosHistorico,setPagosHistorico] = useState([]);

  // Estado modal
  const [mCita,setMCita]       = useState(false);
  const [mPac,setMPac]         = useState(false);
  const [mSesion,setMSesion]   = useState(false);
  const [mPago,setMPago]       = useState(false);
  const [mGasto,setMGasto]     = useState(false);
  const [mHistoria,setMHistoria] = useState(false);
  const [pdfVisor,setPdfVisor]  = useState(null); // {url, nombre}
  const [mEditPac,setMEditPac] = useState(false);
  const [mDia,setMDia]         = useState(false);
  const [selPac,setSelPac]     = useState(null);
  const [selCita,setSelCita]   = useState(null);
  const [detPac,setDetPac]     = useState(null);
  const [diaSelec,setDiaSelec] = useState(null);
  const [citasDia,setCitasDia] = useState([]);
  const printRef = useRef();

  // Notificación
  const [notif,setNotif] = useState(null);
  const notify = (msg,type='success') => { setNotif({msg,type}); setTimeout(()=>setNotif(null),3500); };

  // ── Mes string ───────────────────────────────────────────
  const mesStr = `${mes.getFullYear()}-${String(mes.getMonth()+1).padStart(2,'0')}`;

  // ── Google Calendar ───────────────────────────────────────
  const { googleStatus, connectGoogle, disconnectGoogle, pollSync, renewChannel } = useGoogleSync();

  // Manejar ?google=connected tras OAuth y renovar canal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      notify('Google Calendar conectado exitosamente');
      window.history.replaceState({}, '', '/');
    }
    renewChannel();
  }, []); // eslint-disable-line

  // ── Cargar datos ─────────────────────────────────────────
  const cargarCitas = useCallback(async()=>{
    await pollSync();
    const d = await apiFetch(`/citas.php?mes=${mesStr}`);
    if(Array.isArray(d)) setCitas(d);
  },[apiFetch,mesStr,pollSync]);

  const cargarPacientes = useCallback(async()=>{
    const q = search?`q=${encodeURIComponent(search)}`:'';
    const d = await apiFetch(`/pacientes.php?${q}`);
    if(Array.isArray(d)) setPacientes(d);
  },[apiFetch,search]);

  const cargarGastos = useCallback(async()=>{
    const d = await apiFetch(`/gastos.php?mes=${mesStr}`);
    if(Array.isArray(d)) setGastos(d);
  },[apiFetch,mesStr]);

  const cargarPagos = useCallback(async()=>{
    const d = await apiFetch(`/pagos.php?mes=${mesStr}`);
    if(Array.isArray(d)) setPagosHistorico(d);
  },[apiFetch,mesStr]);

  const cargarKpis = useCallback(async()=>{
    const d = await apiFetch(`/dashboard.php?mes=${mesStr}`);
    if(d && typeof d==='object') setKpis(d);
  },[apiFetch,mesStr]);

  const cargarDetPac = useCallback(async(id)=>{
    const d = await apiFetch(`/pacientes.php?id=${id}`);
    if(d && d.id) setDetPac(d);
  },[apiFetch]);

  // Carga inicial y cuando cambia el mes
  useEffect(()=>{ cargarCitas(); },[cargarCitas]);
  useEffect(()=>{ cargarPacientes(); },[cargarPacientes]);
  useEffect(()=>{ cargarGastos(); },[cargarGastos]);
  useEffect(()=>{ cargarPagos(); },[cargarPagos]);
  useEffect(()=>{ cargarKpis(); },[cargarKpis]); // siempre carga kpis

  // ── Citas de hoy (zona horaria local) ────────────────────
  const hoy = localHoy();
  const citasHoy = citas.filter(c=>c.fecha===hoy);


  // ── Calendario ────────────────────────────────────────────
  const diasMes = ()=>{
    const y=mes.getFullYear(),m=mes.getMonth();
    const first=new Date(y,m,1).getDay(), total=new Date(y,m+1,0).getDate();
    const days=[];
    for(let i=0;i<first;i++) days.push(null);
    for(let i=1;i<=total;i++) days.push(i);
    return days;
  };
  const dStr = dia=>`${mes.getFullYear()}-${String(mes.getMonth()+1).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
  const citasD = dia=>!dia?[]:citas.filter(c=>c.fecha===dStr(dia));
  const abrirDia = dia=>{ if(!dia) return; setDiaSelec(dia); setCitasDia(citasD(dia)); setMDia(true); };

  // ── Formularios ───────────────────────────────────────────
  const [fCita,setFCita]   = useState({paciente_id:'',fecha:hoy,hora:'',duracion:60,tipo:'sesion_regular',modalidad:'presencial_consultorio',estado:'pendiente',notas:'',meet_link:''});
  const [mEditCita,setMEditCita] = useState(false);
  const [citaEditando,setCitaEditando] = useState(null);
  const [fPac,setFPac]     = useState({nombre:'',apellido:'',email:'',whatsapp:'+57',diagnostico:'',motivo_consulta:'',costo_sesion:80000,estado:'activo',genero:'',fecha_nacimiento:'',ocupacion:'',direccion:'',contacto_emergencia:'',telefono_emergencia:''});
  const [fEditPac,setFEditPac] = useState({});
  const [fSes,setFSes]     = useState({tema:'',duracion:60,notas:'',tecnicas_utilizadas:'',estado_emocional:'',tareas_asignadas:'',objetivos_sesion:'',proxima_sesion_recomendada:'',diagnosticos:''});
  const [fPago,setFPago]   = useState({monto:'',metodo:'efectivo',fecha:hoy,notas:'',gastos_asociados:[]});
  const GASTOS_COMUNES = [
    {id:'consultorio',nombre:'Pago consultorio',monto:25000},
    {id:'otro',nombre:'Otro gasto',monto:0}
  ];
  const [fGasto,setFGasto] = useState({categoria:'',descripcion:'',monto:'',fecha:hoy,notas:''});

  // ── Guardar ───────────────────────────────────────────────
  const FCITA_INIT = {paciente_id:'',fecha:hoy,hora:'',duracion:60,tipo:'sesion_regular',modalidad:'presencial_consultorio',estado:'pendiente',notas:'',meet_link:''};

  const guardarCita = async e=>{
    e.preventDefault();
    const r = await apiFetch('/citas.php',{method:'POST',body:JSON.stringify(fCita)});
    if(r?.id){ notify('Cita agendada'); setMCita(false); setMDia(false); await cargarCitas(); await cargarKpis();
      setFCita(FCITA_INIT); }
    else notify('Error al guardar cita','error');
  };

  const guardarEditCita = async e=>{
    e.preventDefault();
    const r = await apiFetch(`/citas.php?id=${citaEditando.id}`,{method:'PUT',body:JSON.stringify(fCita)});
    if(r?.mensaje){ notify('Cita actualizada'); setMEditCita(false); setCitaEditando(null); setFCita(FCITA_INIT); await cargarCitas(); await cargarKpis(); }
    else notify(r?.error||'Error al actualizar','error');
  };

  const guardarPac = async e=>{
    e.preventDefault();
    const r = await apiFetch('/pacientes.php',{method:'POST',body:JSON.stringify(fPac)});
    if(r?.id){ notify('Paciente registrado'); setMPac(false); await cargarPacientes();
      setFPac({nombre:'',apellido:'',email:'',whatsapp:'+57',diagnostico:'',motivo_consulta:'',costo_sesion:80000,estado:'activo',genero:'',fecha_nacimiento:'',ocupacion:'',direccion:'',contacto_emergencia:'',telefono_emergencia:''}); }
    else notify('Error al guardar','error');
  };

  const guardarEditPac = async e=>{
    e.preventDefault();
    const r = await apiFetch(`/pacientes.php?id=${fEditPac.id}`,{method:'PUT',body:JSON.stringify(fEditPac)});
    if(r?.mensaje){ notify('Paciente actualizado'); setMEditPac(false); await cargarPacientes(); await cargarDetPac(fEditPac.id); }
    else notify('Error al actualizar','error');
  };

  const guardarSesion = async e=>{
    e.preventDefault();
    const body={...fSes,paciente_id:selPac?.id,fecha:hoy,cita_id:selCita?.id||null};
    const r = await apiFetch('/sesiones.php',{method:'POST',body:JSON.stringify(body)});
    if(r?.id){ notify('Sesión registrada'); setMSesion(false);
      await cargarCitas(); await cargarKpis();
      if(detPac?.id) await cargarDetPac(detPac.id);
      setFSes({tema:'',duracion:60,notas:'',tecnicas_utilizadas:'',estado_emocional:'',tareas_asignadas:'',objetivos_sesion:'',proxima_sesion_recomendada:'',diagnosticos:''}); }
    else notify('Error al guardar','error');
  };

  const guardarPago = async e=>{
    e.preventDefault();
    const r = await apiFetch('/pagos.php',{method:'POST',body:JSON.stringify({...fPago,paciente_id:selPac?.id})});
    if(r?.id){ notify('Pago registrado'); setMPago(false);
      await cargarPacientes(); await cargarKpis();
      if(detPac?.id) await cargarDetPac(detPac.id);
      setFPago({monto:'',metodo:'efectivo',fecha:hoy,notas:'',gastos_asociados:[]}); }
    else notify('Error al guardar','error');
  };

  const guardarGasto = async e=>{
    e.preventDefault();
    const r = await apiFetch('/gastos.php',{method:'POST',body:JSON.stringify(fGasto)});
    if(r?.id){ notify('Gasto registrado'); setMGasto(false); await cargarGastos(); await cargarKpis();
      setFGasto({categoria:'',descripcion:'',monto:'',fecha:hoy,notas:''}); }
    else notify('Error al guardar','error');
  };

  const eliminarGasto = async id=>{
    if(!window.confirm('¿Eliminar este gasto?')) return;
    await apiFetch(`/gastos.php?id=${id}`,{method:'DELETE'});
    notify('Gasto eliminado'); await cargarGastos(); await cargarKpis();
  };
  const eliminarCita = async c=>{
    if(!window.confirm(`¿Eliminar la cita de ${c.paciente_nombre}? Esta acción no se puede deshacer.`)) return;
    await apiFetch(`/citas.php?id=${c.id}`,{method:'DELETE'});
    notify('Cita eliminada'); await cargarCitas(); await cargarKpis();
  };

  const eliminarPago = async id=>{
    if(!window.confirm('¿Eliminar este pago?')) return;
    await apiFetch(`/pagos.php?id=${id}`,{method:'DELETE'});
    notify('Pago eliminado'); await cargarPagos(); await cargarPacientes(); await cargarKpis();
    if(detPac?.id) await cargarDetPac(detPac.id);
  };

  // ── Documentos ────────────────────────────────────────────
  const subirDocumento = async (pacienteId, tipo, archivo) => {
    const fd = new FormData();
    fd.append('paciente_id', pacienteId);
    fd.append('tipo', tipo);
    fd.append('archivo', archivo);
    const r = await apiUpload('/documentos.php', fd);
    if (r?.id) { notify('Documento guardado'); await cargarDetPac(pacienteId); }
    else notify('Error al subir documento', 'error');
  };

  const eliminarDocumento = async (docId, pacienteId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    await apiFetch(`/documentos.php?id=${docId}`, {method:'DELETE'});
    notify('Documento eliminado'); await cargarDetPac(pacienteId);
  };

  const descargarDocumento = async (docId, nombreOriginal) => {
    const res = await fetch(`${API_BASE}/documentos.php?id=${docId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) { notify('Error al descargar', 'error'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = nombreOriginal; a.click();
    URL.revokeObjectURL(url);
  };

  const verDocumento = async (docId, nombreOriginal) => {
    const res = await fetch(`${API_BASE}/documentos.php?id=${docId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) { notify('Error al cargar documento', 'error'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    setPdfVisor({ url, nombre: nombreOriginal, docId });
  };

  const cambiarEstado = async(id,estadoNuevo)=>{
    const res = await apiFetch(`/citas.php?id=${id}`,{method:'PUT',body:JSON.stringify({estado:estadoNuevo})});
    if(res && res.mensaje){
      notify('Estado actualizado');
      setCitas(prev=>prev.map(c=>c.id===id?{...c,estado:estadoNuevo}:c));
      setCitasDia(prev=>prev.map(c=>c.id===id?{...c,estado:estadoNuevo}:c));
    }
  };

  // ── Bloque HTML de datos del paciente (reutilizado en ambas impresiones) ──

  // ── Impresión sesión individual ───────────────────────────
  const imprimirSesion = async (s,idx)=>{
    const profesional = await getProfesionalData(user?.id, window.location.origin);
    generarPDFSesionIndividual({
      paciente: detPac,
      sesion: s,
      indice: idx,
      profesional,
      baseUrl: window.location.origin
    });
  };

  // ── Impresión historia completa ───────────────────────────
  const imprimirHistoriaCompleta = async ()=>{
    if(!detPac) return;
    const profesional = await getProfesionalData(user?.id, window.location.origin);
    generarPDFHistoriaCompleta({
      paciente: detPac,
      sesiones: detPac.sesiones || [],
      profesional,
      baseUrl: window.location.origin
    });
  };

  // ── Totales ───────────────────────────────────────────────
  const totalPagos  = pagosHistorico.reduce((s,p)=>s+parseFloat(p.monto||0),0);
  const totalGastos = gastos.reduce((s,g)=>s+parseFloat(g.monto||0),0);

  // ════════════════════════════════════════════════════════════
  return(
    <div style={{minHeight:'100vh',background:C.crema,fontFamily:"'MyriadPro',Arial,sans-serif"}}>

      {/* Notif */}
      {notif&&(
        <div style={{position:'fixed',top:'1rem',right:'1rem',zIndex:2000,background:notif.type==='success'?'#D1FAE5':'#FEE2E2',color:notif.type==='success'?'#065F46':'#991B1B',borderRadius:'12px',padding:'0.75rem 1.2rem',boxShadow:'0 4px 20px rgba(0,0,0,0.12)',fontWeight:600,fontSize:'0.88rem',display:'flex',alignItems:'center',gap:'0.5rem'}}>
          {notif.type==='success'?<CheckCircle size={16}/>:<AlertCircle size={16}/>} {notif.msg}
        </div>
      )}

      {/* Header */}
      <header style={{background:C.white,borderBottom:`1px solid ${C.border}`,position:'sticky',top:0,zIndex:100,boxShadow:'0 2px 10px rgba(45,79,74,0.06)'}}>
        <div style={{maxWidth:'1300px',margin:'0 auto',padding:isMobile?'0.6rem 0.85rem':'0.8rem 1.5rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:'0.8rem'}}>
            <img src="/images/isotipo.png" alt="DL" style={{height:42}}/>
            <div>
              <div style={{fontWeight:700,fontSize:'1.05rem',color:C.teal}}>Psicóloga Dani Lopera R.</div>
              <div style={{fontSize:'0.73rem',color:C.ocre,fontWeight:500}}>{user?.nombre} · {user?.rol==='psicologa'?'Psicóloga':'Administrador'}</div>
            </div>
          </div>
          <button onClick={logout} style={btn('ghost',true)}><LogOut size={14}/> Salir</button>
        </div>
      </header>

      {/* Nav */}
      <nav style={{background:C.white,borderBottom:`1px solid ${C.border}`,overflowX:'auto'}}>
        <div style={{maxWidth:'1300px',margin:'0 auto',padding:'0 1.5rem',display:'flex'}}>
          {TABS.filter(t=>!t.rol||t.rol===user?.rol).map(({id,icon:Icon,label})=>(
            <button key={id} onClick={()=>setTab(id)} style={{padding:'0.55rem 0.75rem',border:'none',cursor:'pointer',background:'none',fontWeight:tab===id?700:500,color:tab===id?C.ocre:C.gray,borderBottom:tab===id?`2px solid ${C.ocre}`:'2px solid transparent',fontSize:'0.78rem',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:'0.3rem',fontFamily:"'MyriadPro',Arial,sans-serif"}}>
              <Icon size={13}/> {label}
            </button>
          ))}
        </div>
      </nav>

      <main style={{maxWidth:'1300px',margin:'0 auto',padding:isMobile?'0.85rem':'1.5rem',overflowX:'hidden'}}>

        {/* ══ AGENDA + CALENDARIO ══════════════════════════════ */}
        {tab==='agenda'&&(
          <div>
            {/* Cabecera */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'1.2rem',flexWrap:'wrap',gap:'0.5rem'}}>
              <div>
                <h2 style={{fontSize:'1.25rem',fontWeight:700,color:C.teal}}>Agenda</h2>
                <p style={{fontSize:'0.82rem',color:C.gray}}>{new Date().toLocaleDateString('es-CO',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>
              </div>
              <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                {googleStatus?.connected && (
                  <span style={{fontSize:'0.73rem',color:'#16a34a',fontWeight:600,display:'flex',alignItems:'center',gap:'0.25rem'}}>
                    <span style={{width:7,height:7,borderRadius:'50%',background:'#16a34a',display:'inline-block'}}/>
                    Google Calendar
                  </span>
                )}
                <button style={btn('primary')} onClick={()=>{ setFCita(f=>({...f,fecha:hoy})); setMCita(true); }}>
                  <Plus size={16}/> Nueva cita
                </button>
              </div>
            </div>

            {/* ── Vista Lista ── */}
            {!verCal&&(
              <>
                {/* Citas de hoy */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.6rem'}}>
                  <h3 style={{fontWeight:700,color:C.teal,fontSize:'0.9rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                    Hoy · {new Date().toLocaleDateString('es-CO',{day:'numeric',month:'long'})}
                  </h3>
                  <button style={btn(verCal?'teal':'ghost',true)} onClick={()=>setVerCal(!verCal)}>
                    {verCal?<List size={14}/>:<Calendar size={14}/>} {verCal?'Ver lista':'Ver calendario'}
                  </button>
                </div>
                <div style={{...card,overflow:'hidden',marginBottom:'1.5rem'}}>
                  {citasHoy.length===0?(
                    <div style={{padding:'2.5rem',textAlign:'center',color:C.gray}}>
                      <Calendar size={42} style={{margin:'0 auto 0.7rem',opacity:0.22,display:'block'}}/>
                      <p style={{fontWeight:600}}>No hay citas para hoy</p>
                      <p style={{fontSize:'0.82rem',marginTop:'0.25rem'}}>Agenda una nueva cita con el botón de arriba</p>
                    </div>
                  ):citasHoy.map(c=>(
                    <CitaRow key={c.id} c={c} btn={btn} C={C} MESES={['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']}
                      apiFetch={apiFetch} notify={notify} setCitas={setCitas}
                      setCitaEditando={setCitaEditando} setFCita={setFCita} setMEditCita={setMEditCita} eliminarCita={eliminarCita}
                      cargarDetPac={cargarDetPac} setSelPac={setSelPac} setMHistoria={setMHistoria}/>
                  ))}
                </div>

                {/* Nav mes */}
                <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.75rem'}}>
                  <h3 style={{fontWeight:700,color:C.teal,fontSize:'0.9rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                    Próximas — {MESES[mes.getMonth()]} {mes.getFullYear()}
                  </h3>
                  <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()-1))} style={btn('ghost',true)}><ChevronLeft size={13}/></button>
                  <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()+1))} style={btn('ghost',true)}><ChevronRight size={13}/></button>
                </div>
                <div style={{...card,overflow:'hidden'}}>
                  {citas.filter(c=>c.fecha>hoy).length===0?(
                    <div style={{padding:'1.5rem',textAlign:'center',color:C.gray,fontSize:'0.86rem'}}>Sin citas próximas este mes</div>
                  ):citas.filter(c=>c.fecha>hoy).slice(0,12).map(c=>(
                    <CitaRow key={c.id} c={c} btn={btn} C={C} MESES={['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']}
                      apiFetch={apiFetch} notify={notify} setCitas={setCitas}
                      setCitaEditando={setCitaEditando} setFCita={setFCita} setMEditCita={setMEditCita} eliminarCita={eliminarCita} cambiarEstado={cambiarEstado}
                      cargarDetPac={cargarDetPac} setSelPac={setSelPac} setMHistoria={setMHistoria}/>
                  ))}
                </div>
              </>
            )}

            {/* ── Vista Calendario ── */}
            {verCal&&(
              <div>
                {/* Botón volver a lista */}
                <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'0.6rem'}}>
                  <button style={btn('ghost',true)} onClick={()=>setVerCal(false)}>
                    <List size={14}/> Ver lista
                  </button>
                </div>
                {/* Controles navegación */}
                <div style={{display:'flex',flexDirection:isMobile?'column':'row',justifyContent:'space-between',alignItems:isMobile?'stretch':'center',marginBottom:'0.75rem',gap:'0.5rem'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.5rem'}}>
                    <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()-1))} style={btn('ghost',true)}><ChevronLeft size={15}/></button>
                    <span style={{fontWeight:700,color:C.teal,flex:1,textAlign:'center',fontSize:isMobile?'1rem':'0.97rem'}}>{MESES[mes.getMonth()]} {mes.getFullYear()}</span>
                    <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()+1))} style={btn('ghost',true)}><ChevronRight size={15}/></button>
                    <button onClick={()=>setMes(new Date())} style={btn('ghost',true)}>Hoy</button>
                  </div>
                  {!isMobile&&(
                    <div style={{display:'flex',gap:'0.75rem',fontSize:'0.76rem',color:C.gray,alignItems:'center',flexWrap:'wrap'}}>
                      {[{bg:'#D1FAE5',l:'Confirmada'},{bg:C.ocreLight,l:'Pendiente'},{bg:'#FEE2E2',l:'Cancelada'}].map(({bg,l})=>(
                        <span key={l} style={{display:'flex',alignItems:'center',gap:'0.3rem'}}><span style={{width:12,height:12,borderRadius:3,background:bg,border:`1px solid ${C.border}`,display:'inline-block'}}/>{l}</span>
                      ))}
                      <span style={{color:C.ocre,fontWeight:500}}>· Clic en el día para ver/agregar</span>
                    </div>
                  )}
                  {isMobile&&(
                    <div style={{display:'flex',gap:'0.6rem',fontSize:'0.72rem',color:C.gray,alignItems:'center',justifyContent:'center'}}>
                      {[{bg:'#D1FAE5',l:'Conf.'},{bg:C.ocreLight,l:'Pend.'},{bg:'#FEE2E2',l:'Canc.'}].map(({bg,l})=>(
                        <span key={l} style={{display:'flex',alignItems:'center',gap:'0.25rem'}}><span style={{width:10,height:10,borderRadius:2,background:bg,border:`1px solid ${C.border}`,display:'inline-block'}}/>{l}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{...card,overflow:'hidden'}}>
                  {/* Header días */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:`1px solid ${C.border}`}}>
                    {(isMobile?['D','L','M','Mi','J','V','S']:DIAS_SEM).map((d,i)=>(
                      <div key={i} style={{textAlign:'center',fontSize:isMobile?'0.7rem':'0.74rem',fontWeight:700,color:C.gray,padding:isMobile?'0.4rem 0':'0.55rem 0',background:C.crema}}>{d}</div>
                    ))}
                  </div>
                  {/* Días */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
                    {diasMes().map((dia,i)=>{
                      const cd=citasD(dia), esHoy=dia&&dStr(dia)===hoy;
                      return(
                        <div key={i} onClick={()=>abrirDia(dia)}
                          style={{
                            minHeight:isMobile?56:95,
                            padding:isMobile?'0.25rem 0.2rem':'0.35rem 0.4rem',
                            borderRight:(i+1)%7!==0?`1px solid ${C.border}`:'none',
                            borderBottom:`1px solid ${C.border}`,
                            background:!dia?'#F9F9F9':esHoy?C.ocreLight:C.white,
                            cursor:dia?'pointer':'default',
                            transition:'background 0.12s'
                          }}
                          onMouseEnter={e=>{ if(dia) e.currentTarget.style.background=esHoy?'#EDD8C0':C.rosaLight; }}
                          onMouseLeave={e=>{ if(dia) e.currentTarget.style.background=esHoy?C.ocreLight:C.white; }}
                        >
                          {dia&&(
                            <>
                              <div style={{display:'flex',flexDirection:isMobile?'column':'row',justifyContent:'space-between',alignItems:isMobile?'center':'center',marginBottom:'0.15rem',gap:isMobile?'0.1rem':0}}>
                                <span style={esHoy
                                  ?{background:C.ocre,color:'#fff',borderRadius:'50%',width:isMobile?20:22,height:isMobile?20:22,display:'flex',alignItems:'center',justifyContent:'center',fontSize:isMobile?'0.68rem':'0.75rem',fontWeight:700}
                                  :{fontSize:isMobile?'0.75rem':'0.82rem',fontWeight:500,color:C.teal}
                                }>
                                  {dia}
                                </span>
                                {cd.length>0&&(
                                  <span style={{fontSize:'0.6rem',background:C.rosa+'88',color:C.teal,borderRadius:8,padding:'0 3px',fontWeight:700,lineHeight:'1.4'}}>
                                    {cd.length}
                                  </span>
                                )}
                              </div>
                              {/* Móvil: puntos de color por cita | Desktop: etiquetas con texto */}
                              {isMobile?(
                                <div style={{display:'flex',flexWrap:'wrap',gap:2,justifyContent:'center',marginTop:2}}>
                                  {cd.slice(0,4).map(c=>(
                                    <span key={c.id} style={{width:7,height:7,borderRadius:'50%',display:'inline-block',background:c.estado==='confirmada'?'#10B981':c.estado==='cancelada'?'#EF4444':C.ocre}}/>
                                  ))}
                                  {cd.length>4&&<span style={{fontSize:'0.5rem',color:C.gray,lineHeight:'7px'}}>+</span>}
                                </div>
                              ):(
                                <>
                                  {cd.slice(0,3).map(c=>(
                                    <div key={c.id} style={{fontSize:'0.64rem',background:c.estado==='confirmada'?'#D1FAE5':c.estado==='cancelada'?'#FEE2E2':C.ocreLight,color:C.teal,borderRadius:3,padding:'1px 4px',marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:600}}>
                                      {c.hora?.slice(0,5)} {c.paciente_nombre?.split(' ')[0]}
                                    </div>
                                  ))}
                                  {cd.length>3&&<div style={{fontSize:'0.6rem',color:C.gray,paddingLeft:3}}>+{cd.length-3} más</div>}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Resumen */}
                <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(auto-fit,minmax(140px,1fr))',gap:'0.5rem',marginTop:'0.75rem'}}>
                  {[{l:'Total',v:citas.length,c:C.teal},{l:'Confirmadas',v:citas.filter(c=>c.estado==='confirmada').length,c:C.success},{l:'Pendientes',v:citas.filter(c=>c.estado==='pendiente').length,c:C.warning},{l:'Realizadas',v:citas.filter(c=>c.estado==='realizada').length,c:C.ocre}].map(({l,v,c})=>(
                    <div key={l} style={{background:C.white,borderRadius:11,padding:isMobile?'0.6rem':'0.75rem',textAlign:'center',border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:'0.72rem',color:C.gray,marginBottom:'0.1rem'}}>{l}</div>
                      <div style={{fontSize:isMobile?'1.2rem':'1.4rem',fontWeight:700,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ PACIENTES ═══════════════════════════════════════ */}
        {tab==='pacientes'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.85rem'}}>
              <h2 style={{fontWeight:700,fontSize:'1.25rem',color:C.teal}}>Pacientes</h2>
              <button style={btn('primary')} onClick={()=>setMPac(true)}><Plus size={16}/> Nuevo paciente</button>
            </div>
            <div style={{position:'relative',marginBottom:'0.65rem'}}>
              <Search size={15} style={{position:'absolute',left:'0.85rem',top:'50%',transform:'translateY(-50%)',color:C.gray}}/>
              <input style={{...inp,paddingLeft:'2.4rem'}} placeholder="Buscar por nombre, email o diagnóstico..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            {/* Filtros de estado */}
            <div style={{display:'flex',gap:'0.35rem',marginBottom:'1rem',flexWrap:'wrap',alignItems:'center'}}>
              {[{v:'',l:'Todos'},{v:'formulario_pendiente',l:'Por revisar'},{v:'activo',l:'Activos'},{v:'pausa',l:'En pausa'},{v:'inactivo',l:'Inactivo'},{v:'alta',l:'Alta'}].map(({v,l})=>{
                const activo = filtroPac===v;
                const esPendiente = v==='formulario_pendiente';
                const count = v===''?0:pacientes.filter(p=>p.estado===v).length;
                const tieneAlertas = esPendiente && count>0;
                return(
                  <button key={v} onClick={()=>setFiltroPac(v)} style={{
                    padding:'0.3rem 0.85rem',borderRadius:20,cursor:'pointer',
                    fontFamily:"'MyriadPro',Arial,sans-serif",transition:'all 0.15s',
                    fontSize:'0.78rem',fontWeight:(activo||tieneAlertas)?700:500,
                    border:`1.5px solid ${activo?C.ocre:tieneAlertas?C.danger:C.border}`,
                    background:activo?C.ocreLight:tieneAlertas?'#FEF2F2':'transparent',
                    color:activo?C.ocreDark:tieneAlertas?C.danger:C.gray,
                    boxShadow:tieneAlertas&&!activo?`0 0 0 2px ${C.danger}33`:'none',
                    animation:tieneAlertas&&!activo?'pulse-red 1.5s ease-in-out infinite':'none',
                  }}>
                    {tieneAlertas&&!activo&&<span style={{marginRight:'0.25rem'}}>🔴</span>}{l}
                    {v!==''&&<span style={{
                      marginLeft:'0.35rem',borderRadius:10,padding:'0 5px',
                      fontSize:'0.68rem',fontWeight:700,
                      background:activo?C.ocre+'33':tieneAlertas?C.danger+'22':C.border,
                      color:activo?C.ocreDark:tieneAlertas?C.danger:C.gray,
                    }}>{count}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{...card,overflow:'hidden'}}>
              {(()=>{ const lista=pacientes.filter(p=>filtroPac===''||p.estado===filtroPac); return lista.length===0?(
                <div style={{padding:'2.5rem',textAlign:'center',color:C.gray}}>
                  <Users size={42} style={{margin:'0 auto 0.7rem',opacity:0.22,display:'block'}}/>
                  <p style={{fontWeight:600}}>No se encontraron pacientes</p>
                </div>
              ):lista.map((p,i)=>(
                <div key={p.id} style={{padding:'0.6rem 1.1rem',borderTop:i>0?`1px solid ${C.border}`:'none',display:'flex',alignItems:'center',gap:'0.65rem',flexWrap:'wrap',transition:'background 0.12s'}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.rosaLight}
                  onMouseLeave={e=>e.currentTarget.style.background=''}
                >
                  {/* Avatar */}
                  <div onClick={async()=>{ await cargarDetPac(p.id); setSelPac(p); setMHistoria(true); }}
                    style={{width:36,height:36,borderRadius:'50%',background:`linear-gradient(135deg,${C.rosa},${C.ocre})`,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:'0.95rem',flexShrink:0,cursor:'pointer'}}>
                    {p.nombre?.charAt(0)}
                  </div>
                  {/* Info */}
                  <div style={{flex:'1 1 140px',minWidth:0,cursor:'pointer'}} onClick={async()=>{ await cargarDetPac(p.id); setSelPac(p); setMHistoria(true); }}>
                    <div style={{fontWeight:700,color:C.teal,fontSize:'0.86rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.nombre} {p.apellido}</div>
                    <div style={{fontSize:'0.71rem',color:C.gray,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {p.email&&<span style={{marginRight:'0.5rem'}}>{p.email}</span>}
                      {p.whatsapp&&<span style={{marginRight:'0.5rem'}}>{p.whatsapp}</span>}
                      <span>{p.total_sesiones??0} sesiones</span>
                      {p.ultima_sesion&&<span style={{marginLeft:'0.4rem',color:C.ocre}}>· última: {fmtFecha(p.ultima_sesion)}</span>}
                      {p.proxima_cita_fecha&&<span style={{marginLeft:'0.4rem',color:C.success,fontWeight:600}}>· próxima: {fmtFecha(p.proxima_cita_fecha)} {p.proxima_cita_hora?.slice(0,5)}</span>}
                    </div>
                  </div>
                  {/* Badge estado */}
                  <span style={{...badge(pColor(p.estado)),flexShrink:0}}>{p.estado==='formulario_pendiente'?'por revisar':p.estado}</span>
                  {/* Botones */}
                  <div style={{display:'flex',gap:'0.3rem',flexShrink:0}} onClick={e=>e.stopPropagation()}>
                    {p.estado==='formulario_pendiente'?(
                      <>
                        <button style={{...btn('success',true),padding:'0.28rem 0.5rem'}} onClick={async()=>{
                          await apiFetch(`/pacientes.php?id=${p.id}`,{method:'PUT',body:JSON.stringify({estado:'activo'})});
                          notify(`${p.nombre} activado`); await cargarPacientes();
                        }}><CheckCircle size={12}/></button>
                        {p.whatsapp&&(
                          <a href={`https://wa.me/57${p.whatsapp.replace(/^\+/,'').replace(/^57/,'')}?text=Hola%20${p.nombre},%20soy%20Dani%20Lopera.%20Recibí%20tu%20formulario%20y%20me%20gustaría%20coordinar%20una%20cita.`} target="_blank" rel="noopener noreferrer" style={{...btn('whatsapp',true),textDecoration:'none',padding:'0.28rem 0.5rem'}}><MessageCircle size={12}/></a>
                        )}
                      </>
                    ):(
                      <>
                        {p.whatsapp
                          ? <a href={`https://wa.me/57${p.whatsapp.replace(/^\+/,'').replace(/^57/,'')}?text=Hola%20${p.nombre},%20soy%20Dani%20Lopera,%20tu%20psicóloga.`} target="_blank" rel="noopener noreferrer" style={{...btn('whatsapp',true),textDecoration:'none',padding:'0.28rem 0.5rem'}}><MessageCircle size={12}/></a>
                          : <span style={{width:30,display:'inline-block'}}/>
                        }
                        <button style={{...btn('primary',true),padding:'0.28rem 0.5rem'}} onClick={()=>{ setSelPac(p); setMPago(true); }}><DollarSign size={12}/></button>
                        <button style={{...btn('ghost',true),padding:'0.28rem 0.5rem'}} onClick={()=>{ setSelPac(p); setFCita(f=>({...f,paciente_id:p.id,fecha:hoy})); setMCita(true); }}><Calendar size={12}/></button>
                      </>
                    )}
                  </div>
                </div>
              )); })()}
            </div>
          </div>
        )}

        {/* ══ FINANZAS ════════════════════════════════════════ */}
        {tab==='finanzas'&&(
          <div>
            <div style={{display:'flex',gap:'0.25rem',background:C.white,padding:'0.4rem',borderRadius:12,border:`1px solid ${C.border}`,marginBottom:'1.2rem',width:'fit-content'}}>
              {['ingresos','gastos','balance'].map(st=>(
                <button key={st} onClick={()=>setSubTab(st)} style={{padding:'0.45rem 1.1rem',borderRadius:9,border:'none',cursor:'pointer',fontWeight:600,fontSize:'0.85rem',fontFamily:"'MyriadPro',Arial,sans-serif",background:subTab===st?`linear-gradient(135deg,${C.ocre},${C.ocreDark})`:'transparent',color:subTab===st?'#fff':C.gray}}>
                  {st==='ingresos'?'Ingresos':st==='gastos'?'Gastos':'Balance'}
                </button>
              ))}
            </div>

            {subTab==='ingresos'&&(
              <div>
                {/* Encabezado con mes y total */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.85rem',flexWrap:'wrap',gap:'0.5rem'}}>
                  <div>
                    <h3 style={{fontWeight:700,color:C.teal,marginBottom:'0.1rem'}}>Cuadre de caja</h3>
                    <div style={{display:'flex',alignItems:'center',gap:'0.4rem'}}>
                      <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()-1))} style={btn('ghost',true)}><ChevronLeft size={13}/></button>
                      <span style={{fontWeight:600,color:C.gray,fontSize:'0.85rem'}}>{MESES[mes.getMonth()]} {mes.getFullYear()}</span>
                      <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()+1))} style={btn('ghost',true)}><ChevronRight size={13}/></button>
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:'0.72rem',color:C.gray}}>Total del mes</div>
                    <div style={{fontWeight:700,color:C.success,fontSize:'1.2rem'}}>{fmt(pagosHistorico.reduce((s,p)=>s+parseFloat(p.monto||0),0))}</div>
                  </div>
                </div>

                {/* Lista de pagos */}
                <div style={{...card,overflow:'hidden'}}>
                  {/* Header tabla */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr auto auto auto',gap:'0.5rem',padding:'0.55rem 1rem',background:C.crema,borderBottom:`1px solid ${C.border}`,fontSize:'0.7rem',fontWeight:700,color:C.gray,textTransform:'uppercase',letterSpacing:'0.04em'}}>
                    <span>Paciente</span>
                    <span style={{textAlign:'center'}}>Método</span>
                    <span style={{textAlign:'right'}}>Monto</span>
                    <span/>
                  </div>
                  {pagosHistorico.length===0?(
                    <div style={{padding:'2.5rem',textAlign:'center',color:C.gray}}>
                      <DollarSign size={36} style={{margin:'0 auto 0.5rem',opacity:0.2,display:'block'}}/>
                      <p style={{fontWeight:600}}>Sin pagos en {MESES[mes.getMonth()]}</p>
                    </div>
                  ):pagosHistorico.map((p,i)=>(
                    <div key={p.id} style={{display:'grid',gridTemplateColumns:'1fr auto auto auto',gap:'0.5rem',alignItems:'center',padding:'0.75rem 1rem',borderTop:i>0?`1px solid ${C.border}`:'none'}}>
                      <div>
                        <div style={{fontWeight:600,color:C.teal,fontSize:'0.88rem'}}>{p.paciente_nombre}</div>
                        <div style={{fontSize:'0.73rem',color:C.gray}}>{fmtFecha(p.fecha)}{p.notas&&` · ${p.notas}`}</div>
                      </div>
                      <span style={{...badge(p.metodo==='transferencia'?'blue':p.metodo==='tarjeta'?'amber':'green'),textTransform:'capitalize',whiteSpace:'nowrap'}}>{p.metodo}</span>
                      <span style={{fontWeight:700,color:C.success,whiteSpace:'nowrap'}}>{fmt(p.monto)}</span>
                      <button style={btn('danger',true)} onClick={()=>eliminarPago(p.id)}><Trash2 size={13}/></button>
                    </div>
                  ))}
                  {/* Pie con total */}
                  {pagosHistorico.length>0&&(
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.75rem 1rem',borderTop:`2px solid ${C.border}`,background:C.crema}}>
                      <span style={{fontSize:'0.8rem',color:C.gray}}>{pagosHistorico.length} {pagosHistorico.length===1?'pago':'pagos'}</span>
                      <span style={{fontWeight:700,color:C.success}}>{fmt(pagosHistorico.reduce((s,p)=>s+parseFloat(p.monto||0),0))}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {subTab==='gastos'&&(
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
                  <div>
                    <h3 style={{fontWeight:700,color:C.teal}}>Gastos del consultorio</h3>
                    <p style={{fontSize:'0.8rem',color:C.gray}}>{MESES[mes.getMonth()]} {mes.getFullYear()}</p>
                  </div>
                  <button style={btn('primary')} onClick={()=>setMGasto(true)}><Plus size={16}/> Nuevo gasto</button>
                </div>
                <div style={{...card,overflow:'hidden'}}>
                  {gastos.length===0?(
                    <div style={{padding:'2rem',textAlign:'center',color:C.gray}}>Sin gastos registrados este mes</div>
                  ):<>
                    {gastos.map((g,i)=>(
                      <div key={g.id} style={{padding:'0.9rem 1.4rem',borderTop:i>0?`1px solid ${C.border}`:'none',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <div>
                          <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                            <span style={badge('amber')}>{g.categoria}</span>
                            <span style={{fontWeight:600,color:C.teal}}>{g.descripcion}</span>
                          </div>
                          <div style={{fontSize:'0.75rem',color:C.gray,marginTop:'0.15rem'}}>{fmtFecha(g.fecha)} · {g.registrado_por}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:'0.7rem'}}>
                          <span style={{fontWeight:700,color:C.danger}}>{fmt(g.monto)}</span>
                          <button style={btn('danger',true)} onClick={()=>eliminarGasto(g.id)}><Trash2 size={13}/></button>
                        </div>
                      </div>
                    ))}
                    <div style={{padding:'0.9rem 1.4rem',borderTop:`2px solid ${C.border}`,display:'flex',justifyContent:'space-between',background:C.crema}}>
                      <span style={{fontWeight:700,color:C.teal}}>Total gastos</span>
                      <span style={{fontWeight:700,color:C.danger}}>{fmt(totalGastos)}</span>
                    </div>
                  </>}
                </div>
              </div>
            )}

            {subTab==='balance'&&(
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'1rem'}}>
                {[{l:'Ingresos totales',v:totalPagos,c:C.success,bg:'#D1FAE5'},{l:'Gastos totales',v:totalGastos,c:C.danger,bg:'#FEE2E2'},{l:'Balance neto',v:totalPagos-totalGastos,c:totalPagos-totalGastos>=0?C.teal:C.danger,bg:totalPagos-totalGastos>=0?C.tealLight:'#FEE2E2'}].map(({l,v,c,bg})=>(
                  <div key={l} style={{borderRadius:16,padding:'2rem',textAlign:'center',background:bg,border:`1px solid ${c}44`}}>
                    <div style={{fontSize:'0.77rem',color:c,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:'0.5rem'}}>{l}</div>
                    <div style={{fontSize:'1.9rem',fontWeight:700,color:c}}>{fmt(v)}</div>
                    {l==='Balance neto'&&<div style={{fontSize:'0.8rem',color:c,marginTop:'0.4rem'}}>{v>=0?'✓ Superávit':'✗ Déficit'}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ DASHBOARD ═══════════════════════════════════════ */}
        {tab==='dashboard'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.2rem'}}>
              <h2 style={{fontWeight:700,fontSize:'1.25rem',color:C.teal}}>Dashboard</h2>
              <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()-1))} style={btn('ghost',true)}><ChevronLeft size={14}/></button>
                <span style={{fontWeight:600,color:C.teal,fontSize:'0.88rem'}}>{MESES[mes.getMonth()]} {mes.getFullYear()}</span>
                <button onClick={()=>setMes(new Date(mes.getFullYear(),mes.getMonth()+1))} style={btn('ghost',true)}><ChevronRight size={14}/></button>
                <button onClick={cargarKpis} style={btn('ghost',true)}>↻ Actualizar</button>
              </div>
            </div>
            {kpis?(
              <>
                <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(3,1fr)',gap:'0.6rem',marginBottom:'1rem'}}>
                  <Kpi label="Pacientes activos"  value={kpis.pacientes_activos}  Icon={Users}      color={C.teal}/>
                  <Kpi label="Citas hoy"           value={kpis.citas_hoy}          Icon={Calendar}   color={C.ocre}/>
                  <Kpi label="Sesiones del mes"    value={kpis.sesiones_mes}       Icon={Activity}   color={C.rosaMid}/>
                  <Kpi label="Ingresos del mes"    value={fmt(kpis.ingresos_mes)}  Icon={TrendingUp}  color={C.success}/>
                  <Kpi label="Gastos del mes"      value={fmt(kpis.gastos_mes)}    Icon={DollarSign} color={C.danger}/>
                  <Kpi label="Balance del mes"     value={fmt(kpis.balance_mes)}   Icon={BarChart3}  color={kpis.balance_mes>=0?C.teal:C.danger}/>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1rem'}}>
                  <div style={{...card,padding:'1.25rem'}}>
                    <h3 style={{fontWeight:700,color:C.teal,marginBottom:'1rem',fontSize:'0.93rem'}}>Sesiones por mes</h3>
                    {(kpis.sesiones_por_mes||[]).length===0?<p style={{color:C.gray,fontSize:'0.85rem'}}>Sin datos aún</p>:(
                      <div style={{display:'flex',flexDirection:'column',gap:'0.55rem'}}>
                        {kpis.sesiones_por_mes.map(({mes:m,total})=>{
                          const max=Math.max(...kpis.sesiones_por_mes.map(x=>x.total),1);
                          return(
                            <div key={m} style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                              <span style={{fontSize:'0.75rem',color:C.gray,width:54,flexShrink:0}}>{MESES[parseInt(m.slice(5))-1]?.slice(0,3)} {m.slice(2,4)}</span>
                              <div style={{flex:1,height:17,background:C.crema,borderRadius:9,overflow:'hidden'}}>
                                <div style={{height:'100%',borderRadius:9,background:`linear-gradient(135deg,${C.rosa},${C.ocre})`,width:`${(total/max)*100}%`,transition:'width 0.4s'}}/>
                              </div>
                              <span style={{fontWeight:700,fontSize:'0.79rem',color:C.teal,width:22,textAlign:'right'}}>{total}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{...card,padding:'1.25rem'}}>
                    <h3 style={{fontWeight:700,color:C.teal,marginBottom:'1rem',fontSize:'0.93rem'}}>Estado de pacientes</h3>
                    <div style={{display:'flex',flexDirection:'column',gap:'0.65rem'}}>
                      {[
                        {l:'Activos',       v:kpis.pacientes_activos,   c:C.success},
                        {l:'En pausa',      v:kpis.pacientes_pausa,     c:C.warning},
                        {l:'Alta',          v:kpis.pacientes_alta,      c:'#3B82F6'},
                        {l:'Inactivos',     v:kpis.pacientes_inactivos, c:C.danger},
                        {l:'Por revisar',   v:kpis.pacientes_formulario,c:C.ocre},
                        {l:'Total',         v:kpis.pacientes_total,     c:C.teal},
                      ].map(({l,v,c})=>(
                        <div key={l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.65rem 1rem',borderRadius:10,background:C.crema,border:`1px solid ${C.border}`}}>
                          <span style={{fontSize:'0.85rem',color:C.gray}}>{l}</span>
                          <span style={{fontWeight:700,fontSize:'1.15rem',color:c}}>{v??0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* ── Embudo de pacientes ── */}
                {(()=>{
                  const total = kpis.pacientes_total || 1;
                  const etapas = [
                    { l:'Formularios recibidos', v: kpis.pacientes_formulario||0,  color:'#C4956A', desc:'Prospectos que enviaron el formulario y están por revisar' },
                    { l:'Pacientes activos',      v: kpis.pacientes_activos||0,     color:'#2D4F4A', desc:'Aceptados y en proceso de acompañamiento' },
                    { l:'Con sesiones registradas',v:kpis.pacientes_con_sesion||0,  color:'#3D6560', desc:'Pacientes que tienen al menos una sesión documentada' },
                    { l:'En pausa',               v: kpis.pacientes_pausa||0,       color:'#F59E0B', desc:'Proceso detenido temporalmente' },
                    { l:'Alta exitosa',            v: kpis.pacientes_alta||0,        color:'#10B981', desc:'Proceso completado con éxito' },
                    { l:'Inactivos',              v: kpis.pacientes_inactivos||0,   color:'#EF4444', desc:'Sin continuidad en el proceso' },
                  ];
                  const maxV = Math.max(...etapas.map(e=>e.v), 1);
                  return (
                    <div style={{...card,padding:'1.5rem'}}>
                      <div style={{marginBottom:'1.2rem'}}>
                        <h3 style={{fontWeight:700,color:C.teal,fontSize:'0.93rem',margin:'0 0 0.25rem'}}>Embudo de pacientes</h3>
                        <p style={{fontSize:'0.78rem',color:C.gray,margin:0}}>Distribución a lo largo del proceso de acompañamiento · {kpis.pacientes_total} pacientes en total</p>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:'0.55rem'}}>
                        {etapas.map(({l,v,color,desc})=>{
                          const pct = Math.round((v/maxV)*100);
                          const pctTotal = total>0?Math.round((v/total)*100):0;
                          return (
                            <div key={l}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.3rem'}}>
                                <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                                  <div style={{width:10,height:10,borderRadius:'50%',background:color,flexShrink:0}}/>
                                  <span style={{fontSize:'0.83rem',color:C.teal,fontWeight:600}}>{l}</span>
                                </div>
                                <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                                  <span style={{fontSize:'0.72rem',color:C.gray}}>{pctTotal}% del total</span>
                                  <span style={{fontWeight:700,color,fontSize:'1rem',minWidth:28,textAlign:'right'}}>{v}</span>
                                </div>
                              </div>
                              <div style={{height:8,borderRadius:6,background:C.crema,overflow:'hidden'}}>
                                <div style={{height:'100%',borderRadius:6,background:color,width:`${pct}%`,transition:'width 0.6s ease'}}/>
                              </div>
                              <p style={{fontSize:'0.71rem',color:C.gray,margin:'0.2rem 0 0'}}>{desc}</p>
                            </div>
                          );
                        })}
                      </div>
                      {kpis.promedio_sesiones>0&&(
                        <div style={{marginTop:'1.25rem',paddingTop:'1rem',borderTop:`1px solid ${C.border}`,display:'flex',gap:'1.5rem',flexWrap:'wrap'}}>
                          <div>
                            <div style={{fontSize:'0.7rem',color:C.gray,textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:600}}>Promedio sesiones / paciente</div>
                            <div style={{fontSize:'1.4rem',fontWeight:700,color:C.teal}}>{kpis.promedio_sesiones}</div>
                          </div>
                          <div>
                            <div style={{fontSize:'0.7rem',color:C.gray,textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:600}}>Tasa de continuidad</div>
                            <div style={{fontSize:'1.4rem',fontWeight:700,color:C.success}}>{kpis.pacientes_total>0?Math.round((kpis.pacientes_con_sesion/kpis.pacientes_total)*100):0}%</div>
                          </div>
                          <div>
                            <div style={{fontSize:'0.7rem',color:C.gray,textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:600}}>Tasa de alta exitosa</div>
                            <div style={{fontSize:'1.4rem',fontWeight:700,color:'#10B981'}}>{kpis.pacientes_total>0?Math.round((kpis.pacientes_alta/kpis.pacientes_total)*100):0}%</div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {kpis.gastos_por_categoria?.length>0&&(
                  <div style={{...card,padding:'1.25rem'}}>
                    <h3 style={{fontWeight:700,color:C.teal,marginBottom:'0.85rem',fontSize:'0.93rem'}}>Gastos por categoría — {MESES[mes.getMonth()]}</h3>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(165px,1fr))',gap:'0.55rem'}}>
                      {kpis.gastos_por_categoria.map(({categoria,total})=>(
                        <div key={categoria} style={{padding:'0.75rem',borderRadius:10,background:C.ocreLight,border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:'0.71rem',color:C.ocreDark,textTransform:'uppercase',fontWeight:600,marginBottom:'0.2rem'}}>{categoria}</div>
                          <div style={{fontWeight:700,color:C.teal}}>{fmt(total)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ):(
              <div style={{textAlign:'center',padding:'3rem',color:C.gray}}>
                <BarChart3 size={44} style={{margin:'0 auto 1rem',opacity:0.22,display:'block'}}/>
                <p>Cargando estadísticas...</p>
                <button style={{...btn('primary'),marginTop:'1rem'}} onClick={cargarKpis}>Cargar dashboard</button>
              </div>
            )}
          </div>
        )}

        {/* ══ MI PERFIL ════════════════════════════════════════ */}
        {tab==='perfil'&&(()=>{
          const apiBase = (process.env.REACT_APP_API_URL||'/api').replace('/api','');
          return (
          <TabPerfil apiFetch={apiFetch} apiUpload={apiUpload} notify={notify}
            updateUser={updateUser} btn={btn} inp={inp} C={C} card={card}
            googleStatus={googleStatus} connectGoogle={connectGoogle} disconnectGoogle={disconnectGoogle}
            apiBase={apiBase}/>
          );
        })()}

        {/* ══ CITAS (todas) ════════════════════════════════════ */}
        {tab==='citas'&&(
          <TabCitas apiFetch={apiFetch} notify={notify} btn={btn} inp={inp} C={C} card={card} badge={badge} eColor={eColor}
            cambiarEstado={cambiarEstado} setCitaEditando={setCitaEditando} setFCita={setFCita} setMEditCita={setMEditCita} eliminarCita={eliminarCita}
            setMCita={setMCita} hoy={hoy} cargarDetPac={cargarDetPac} setSelPac={setSelPac} setMHistoria={setMHistoria}/>
        )}

        {/* ══ CUMPLEAÑOS ═══════════════════════════════════════ */}
        {tab==='cumpleanos'&&(
          <TabCumpleanos apiFetch={apiFetch} notify={notify} btn={btn} inp={inp} C={C} card={card} badge={badge} googleStatus={googleStatus}/>
        )}

        {/* ══ USUARIOS (solo admin) ════════════════════════════ */}
        {tab==='usuarios'&&user?.rol==='admin'&&(
          <TabUsuarios apiFetch={apiFetch} notify={notify} btn={btn} inp={inp} F={F} C={C} card={card}/>
        )}

      </main>

      {/* ══ MODALES ═══════════════════════════════════════════ */}

      {/* Modal Día */}
      {mDia&&diaSelec&&(
        <Modal title={`${DIAS_SEM[new Date(dStr(diaSelec)+'T12:00').getDay()]} ${diaSelec} de ${MESES[mes.getMonth()]}`} onClose={()=>setMDia(false)} color={C.teal}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
            <span style={{fontSize:'0.84rem',color:C.gray}}>{citasDia.length} cita(s)</span>
            <button style={btn('primary',true)} onClick={()=>{ setFCita(f=>({...f,fecha:dStr(diaSelec)})); setMDia(false); setMCita(true); }}><Plus size={13}/> Nueva cita aquí</button>
          </div>
          {citasDia.length===0?(
            <div style={{textAlign:'center',padding:'1.5rem',color:C.gray}}><Calendar size={34} style={{margin:'0 auto 0.5rem',opacity:0.22,display:'block'}}/><p style={{fontSize:'0.85rem'}}>Sin citas este día</p></div>
          ):(
            <div style={{display:'flex',flexDirection:'column',gap:'0.6rem'}}>
              {citasDia.map(c=>(
                <div key={c.id} style={{background:C.crema,borderRadius:11,padding:'0.85rem 1rem',border:`1px solid ${C.border}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div>
                      <div style={{fontWeight:700,color:C.teal}}>{c.paciente_nombre}</div>
                      <div style={{fontSize:'0.77rem',color:C.gray,display:'flex',alignItems:'center',gap:'0.4rem',marginTop:'0.15rem'}}><Clock size={11}/> {c.hora?.slice(0,5)} · {c.duracion} min</div>
                    </div>
                    <span style={badge(eColor(c.estado))}>{c.estado}</span>
                  </div>
                  <div style={{fontSize:'0.74rem',color:C.gray,marginTop:'0.25rem'}}>
                    {c.modalidad==='virtual'?'💻 Virtual':c.modalidad==='presencial_jardin'?'🌿 Jardín Botánico':'🏢 Consultorio'}
                  </div>
                  <div style={{display:'flex',gap:'0.4rem',marginTop:'0.6rem',flexWrap:'wrap'}}>
                    {c.whatsapp&&<a href={`https://wa.me/57${c.whatsapp.replace(/^\+/,'').replace(/^57/,'')}`} target="_blank" rel="noopener noreferrer" style={btn('whatsapp',true)}><MessageCircle size={12}/> WhatsApp</a>}
                    {c.modalidad==='virtual'&&c.meet_link&&<a href={c.meet_link} target="_blank" rel="noopener noreferrer" style={{...btn('primary',true),background:'#1a73e8',borderColor:'#1a73e8',textDecoration:'none'}}><Video size={12}/> Meet</a>}
                    <button style={btn('teal',true)} onClick={()=>{ setSelPac({id:c.paciente_id,nombre:c.paciente_nombre}); setSelCita(c); setMDia(false); setMSesion(true); }}><FileText size={12}/> Sesión</button>
                    <button style={btn('ghost',true)} onClick={()=>{ setCitaEditando(c); setFCita({paciente_id:c.paciente_id,fecha:c.fecha,hora:c.hora?.slice(0,5),duracion:c.duracion,tipo:c.tipo,modalidad:c.modalidad||'presencial_consultorio',estado:c.estado,notas:c.notas||'',meet_link:c.meet_link||''}); setMDia(false); setMEditCita(true); }}><Edit2 size={12}/> Editar</button>
                    {c.estado==='pendiente'&&<button style={btn('success',true)} onClick={()=>cambiarEstado(c.id,'confirmada')}><CheckCircle size={12}/> Confirmar</button>}
                    {c.estado!=='cancelada'&&<button style={btn('danger',true)} onClick={()=>cambiarEstado(c.id,'cancelada')}><X size={12}/> Cancelar</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Modal Nueva Cita */}
      {(mCita||mEditCita)&&(
        <Modal title={mEditCita?"Editar cita":"Nueva cita"} onClose={()=>{ setMCita(false); setMEditCita(false); setCitaEditando(null); setFCita(FCITA_INIT); }} color={C.teal}>
          <form onSubmit={mEditCita?guardarEditCita:guardarCita}>
            <F label="Paciente *">
              <select style={inp} value={fCita.paciente_id} onChange={e=>setFCita({...fCita,paciente_id:e.target.value})} required>
                <option value="">Seleccionar paciente...</option>
                {pacientes.map(p=><option key={p.id} value={p.id}>{p.nombre} {p.apellido}</option>)}
              </select>
            </F>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Fecha *"><input style={inp} type="date" value={fCita.fecha} onChange={e=>setFCita({...fCita,fecha:e.target.value})} required/></F>
              <F label="Hora *">
                <select style={inp} value={fCita.hora} onChange={e=>setFCita({...fCita,hora:e.target.value})} required>
                  <option value="">Seleccionar hora...</option>
                  {Array.from({length:32},(_,i)=>{const h=Math.floor(i/2)+7;const m=i%2===0?'00':'30';const val=`${String(h).padStart(2,'0')}:${m}`;return <option key={val} value={val}>{val}</option>;})}
                </select>
              </F>
              <F label="Duración (min)"><input style={inp} type="number" value={fCita.duracion} onChange={e=>setFCita({...fCita,duracion:e.target.value})} min="15" max="180"/></F>
              <F label="Tipo de sesión">
                <select style={inp} value={fCita.tipo} onChange={e=>setFCita({...fCita,tipo:e.target.value})}>
                  <option value="primera_vez">Primera vez</option>
                  <option value="evaluacion">Evaluación</option>
                  <option value="sesion_regular">Sesión regular</option>
                  <option value="seguimiento">Seguimiento</option>
                  <option value="urgente">Urgente</option>
                </select>
              </F>
            </div>
            <F label="Modalidad">
              <select style={inp} value={fCita.modalidad} onChange={e=>setFCita({...fCita,modalidad:e.target.value,meet_link:e.target.value!=='virtual'?'':fCita.meet_link})}>
                <option value="presencial_consultorio">🏢 Presencial — Consultorio</option>
                <option value="presencial_jardin">🌿 Presencial — Jardín Botánico</option>
                <option value="virtual">💻 Virtual (videollamada)</option>
              </select>
            </F>
            {fCita.modalidad==='virtual'&&(
              <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:10,padding:'0.65rem 0.9rem',fontSize:'0.8rem',color:'#1e40af',display:'flex',alignItems:'center',gap:'0.5rem'}}>
                <Video size={14}/>
                {fCita.meet_link
                  ? <span>Enlace Meet: <a href={fCita.meet_link} target="_blank" rel="noopener noreferrer" style={{color:'#1a73e8',fontWeight:600}}>{fCita.meet_link}</a></span>
                  : <span>Se generará automáticamente un enlace de Google Meet al guardar la cita.</span>
                }
              </div>
            )}
            <F label="Notas"><textarea style={{...inp,height:60,resize:'vertical'}} value={fCita.notas} onChange={e=>setFCita({...fCita,notas:e.target.value})}/></F>
            <div style={{display:'flex',gap:'0.75rem',marginTop:'0.5rem'}}>
              <button type="button" onClick={()=>{ setMCita(false); setMEditCita(false); setCitaEditando(null); setFCita(FCITA_INIT); }} style={btn('ghost')}>Cancelar</button>
              <button type="submit" style={{...btn('teal'),flex:1}}>{mEditCita?'Guardar cambios':'Guardar cita'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Nuevo Paciente */}
      {mPac&&(
        <Modal title="Nuevo paciente" onClose={()=>setMPac(false)} color={C.ocre} wide>
          <form onSubmit={guardarPac}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Nombre *"><input style={inp} value={fPac.nombre} onChange={e=>setFPac({...fPac,nombre:e.target.value})} required/></F>
              <F label="Apellido *"><input style={inp} value={fPac.apellido} onChange={e=>setFPac({...fPac,apellido:e.target.value})} required/></F>
              <F label="Email"><input style={inp} type="email" value={fPac.email} onChange={e=>setFPac({...fPac,email:e.target.value})}/></F>
              <F label="WhatsApp"><input style={inp} value={fPac.whatsapp} onChange={e=>setFPac({...fPac,whatsapp:e.target.value})} placeholder="+573001234567"/></F>
              <F label="Fecha de nacimiento"><input style={inp} type="date" value={fPac.fecha_nacimiento} onChange={e=>setFPac({...fPac,fecha_nacimiento:e.target.value})}/></F>
              <F label="Género">
                <select style={inp} value={fPac.genero} onChange={e=>setFPac({...fPac,genero:e.target.value})}>
                  <option value="">Seleccionar...</option>
                  <option value="femenino">Femenino</option>
                  <option value="masculino">Masculino</option>
                  <option value="otro">Otro</option>
                  <option value="prefiero_no_decir">Prefiero no decir</option>
                </select>
              </F>
              <F label="Ocupación"><input style={inp} value={fPac.ocupacion} onChange={e=>setFPac({...fPac,ocupacion:e.target.value})}/></F>
            </div>
            <F label="Dirección"><input style={inp} value={fPac.direccion} onChange={e=>setFPac({...fPac,direccion:e.target.value})}/></F>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Contacto de emergencia"><input style={inp} value={fPac.contacto_emergencia} onChange={e=>setFPac({...fPac,contacto_emergencia:e.target.value})}/></F>
              <F label="Teléfono de emergencia"><input style={inp} value={fPac.telefono_emergencia} onChange={e=>setFPac({...fPac,telefono_emergencia:e.target.value})}/></F>
            </div>
            <F label="Motivo de consulta"><textarea style={{...inp,height:65,resize:'vertical'}} value={fPac.motivo_consulta} onChange={e=>setFPac({...fPac,motivo_consulta:e.target.value})}/></F>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Diagnóstico inicial"><input style={inp} value={fPac.diagnostico} onChange={e=>setFPac({...fPac,diagnostico:e.target.value})}/></F>
              <F label="Costo por sesión (COP)"><input style={inp} type="number" value={fPac.costo_sesion} onChange={e=>setFPac({...fPac,costo_sesion:e.target.value})}/></F>
            </div>
            <div style={{display:'flex',gap:'0.75rem',marginTop:'0.5rem'}}>
              <button type="button" onClick={()=>setMPac(false)} style={btn('ghost')}>Cancelar</button>
              <button type="submit" style={{...btn('primary'),flex:1}}>Registrar paciente</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Sesión */}
      {mSesion&&selPac&&(
        <Modal title={`Sesión · ${selPac.nombre}`} onClose={()=>setMSesion(false)} color={C.teal} wide>
          <form onSubmit={guardarSesion}>
            <F label="Tema de la sesión *"><input style={inp} value={fSes.tema} onChange={e=>setFSes({...fSes,tema:e.target.value})} required placeholder="Ej: Técnicas de respiración"/></F>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Duración (min)"><input style={inp} type="number" value={fSes.duracion} onChange={e=>setFSes({...fSes,duracion:e.target.value})}/></F>
              <F label="Estado emocional">
                <select style={inp} value={fSes.estado_emocional} onChange={e=>setFSes({...fSes,estado_emocional:e.target.value})}>
                  <option value="">Seleccionar...</option>
                  <option value="muy_bajo">Muy bajo</option>
                  <option value="bajo">Bajo</option>
                  <option value="neutro">Neutro</option>
                  <option value="bueno">Bueno</option>
                  <option value="muy_bueno">Muy bueno</option>
                </select>
              </F>
            </div>
            <F label="Motivo de consulta"><input style={inp} value={fSes.tecnicas_utilizadas} onChange={e=>setFSes({...fSes,tecnicas_utilizadas:e.target.value})} placeholder="Ej: Mindfulness, TCC"/></F>
            <F label="Diagnósticos CIE10">
              <DSM5Picker value={fSes.diagnosticos} onChange={(v)=>setFSes({...fSes,diagnosticos:v})} inp={inp} C={C}/>
            </F>
            <F label="Cuadro clínico de atención"><textarea style={{...inp,height:55,resize:'vertical'}} value={fSes.objetivos_sesion} onChange={e=>setFSes({...fSes,objetivos_sesion:e.target.value})}/></F>
            <F label="Resumen de la atención"><textarea style={{...inp,height:85,resize:'vertical'}} value={fSes.notas} onChange={e=>setFSes({...fSes,notas:e.target.value})} placeholder="Observaciones, avances, aspectos relevantes..."/></F>
            <F label="Finalidad, tareas y recomendaciones"><textarea style={{...inp,height:55,resize:'vertical'}} value={fSes.tareas_asignadas} onChange={e=>setFSes({...fSes,tareas_asignadas:e.target.value})}/></F>
            <F label="Próxima sesión recomendada"><input style={inp} type="date" value={fSes.proxima_sesion_recomendada} onChange={e=>setFSes({...fSes,proxima_sesion_recomendada:e.target.value})}/></F>
            <div style={{display:'flex',gap:'0.75rem',marginTop:'0.5rem'}}>
              <button type="button" onClick={()=>setMSesion(false)} style={btn('ghost')}>Cancelar</button>
              <button type="submit" style={{...btn('teal'),flex:1}}>Guardar sesión</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Pago */}
      {mPago&&selPac&&(
        <Modal title={`Pago · ${selPac.nombre}`} onClose={()=>setMPago(false)} color={C.ocre}>
          <form onSubmit={guardarPago}>
            <F label="Monto (COP) *"><input style={inp} type="number" value={fPago.monto} onChange={e=>setFPago({...fPago,monto:e.target.value})} required placeholder="80000"/></F>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Método">
                <select style={inp} value={fPago.metodo} onChange={e=>setFPago({...fPago,metodo:e.target.value})}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="nequi">Nequi</option>
                  <option value="daviplata">Daviplata</option>
                </select>
              </F>
              <F label="Fecha *"><input style={inp} type="date" value={fPago.fecha} onChange={e=>setFPago({...fPago,fecha:e.target.value})} required/></F>
            </div>
            <F label="Notas / Referencia"><input style={inp} value={fPago.notas} onChange={e=>setFPago({...fPago,notas:e.target.value})}/></F>

            <div style={{height:1,background:C.border,margin:'0.8rem 0 0.85rem'}}/>
            <p style={{fontSize:'0.78rem',fontWeight:700,color:C.teal,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'0.5rem'}}>Gastos Asociados</p>
            {GASTOS_COMUNES.map(g=>{
              const existe = fPago.gastos_asociados.find(ga=>ga.id===g.id);
              return (
                <div key={g.id} style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.5rem'}}>
                  <input type="checkbox" checked={!!existe} onChange={e=>{
                    if(e.target.checked){
                      setFPago({...fPago,gastos_asociados:[...fPago.gastos_asociados,{id:g.id,nombre:g.nombre,monto:g.monto}]});
                    }else{
                      setFPago({...fPago,gastos_asociados:fPago.gastos_asociados.filter(ga=>ga.id!==g.id)});
                    }
                  }} style={{cursor:'pointer',width:'18px',height:'18px'}}/>
                  <label style={{cursor:'pointer',flex:1,display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'0.9rem'}}>
                    <span>{g.nombre}</span>
                    <span style={{fontWeight:600,color:C.gold}}>$ {g.monto.toLocaleString('es-CO')}</span>
                  </label>
                </div>
              );
            })}
            {fPago.gastos_asociados.length>0&&(
              <div style={{background:C.goldPale,border:`1px solid ${C.goldLine}`,borderRadius:'8px',padding:'0.6rem',marginTop:'0.8rem'}}>
                <div style={{fontSize:'0.78rem',fontWeight:700,color:C.gold,marginBottom:'0.4rem',textTransform:'uppercase'}}>Total gastos asociados</div>
                <div style={{fontSize:'1.1rem',fontWeight:700,color:C.gold}}>$ {fPago.gastos_asociados.reduce((t,g)=>t+g.monto,0).toLocaleString('es-CO')}</div>
              </div>
            )}

            <div style={{display:'flex',gap:'0.75rem',marginTop:'1rem'}}>
              <button type="button" onClick={()=>setMPago(false)} style={btn('ghost')}>Cancelar</button>
              <button type="submit" style={{...btn('primary'),flex:1}}>Registrar pago</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Gasto */}
      {mGasto&&(
        <Modal title="Nuevo gasto" onClose={()=>setMGasto(false)} color={C.ocreDark}>
          <form onSubmit={guardarGasto}>
            <F label="Categoría *">
              <select style={inp} value={fGasto.categoria} onChange={e=>setFGasto({...fGasto,categoria:e.target.value})} required>
                <option value="">Seleccionar...</option>
                {['arriendo','servicios','materiales','capacitacion','marketing','salud','transporte','tecnologia','otro'].map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
              </select>
            </F>
            <F label="Descripción *"><input style={inp} value={fGasto.descripcion} onChange={e=>setFGasto({...fGasto,descripcion:e.target.value})} required/></F>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Monto (COP) *"><input style={inp} type="number" value={fGasto.monto} onChange={e=>setFGasto({...fGasto,monto:e.target.value})} required/></F>
              <F label="Fecha *"><input style={inp} type="date" value={fGasto.fecha} onChange={e=>setFGasto({...fGasto,fecha:e.target.value})} required/></F>
            </div>
            <F label="Notas"><textarea style={{...inp,height:55,resize:'vertical'}} value={fGasto.notas} onChange={e=>setFGasto({...fGasto,notas:e.target.value})}/></F>
            <div style={{display:'flex',gap:'0.75rem',marginTop:'0.5rem'}}>
              <button type="button" onClick={()=>setMGasto(false)} style={btn('ghost')}>Cancelar</button>
              <button type="submit" style={{...btn('primary'),flex:1}}>Guardar gasto</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Historia Clínica */}
      {mHistoria&&detPac&&(
        <div style={{position:'fixed',inset:0,background:'rgba(45,79,74,0.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:'0.75rem',zIndex:1000,overflowY:'auto'}}>
          <div style={{...card,width:'100%',maxWidth:'740px',maxHeight:'96vh',display:'flex',flexDirection:'column'}}>
            {/* Header */}
            <div style={{background:`linear-gradient(135deg,${C.teal},${C.tealMid})`,color:'#fff',padding:'0.9rem 1rem',borderRadius:'16px 16px 0 0',flexShrink:0}}>
              {/* Fila superior: avatar + nombre + X */}
              <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                <div style={{width:40,height:40,minWidth:40,borderRadius:'50%',background:'rgba(255,255,255,0.25)',border:'2px solid rgba(255,255,255,0.4)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'1.05rem',flexShrink:0}}>
                  {detPac.nombre?.charAt(0)}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:'0.95rem',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{detPac.nombre} {detPac.apellido}</div>
                  <div style={{fontSize:'0.72rem',opacity:0.8}}>Historia Clínica · ID {String(detPac.id).padStart(4,'0')}</div>
                </div>
                <button onClick={()=>setMHistoria(false)} style={{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',borderRadius:8,cursor:'pointer',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',width:32,height:32,flexShrink:0}}>
                  <X size={17}/>
                </button>
              </div>
              {/* Fila inferior: botones acción */}
              <div style={{display:'flex',gap:'0.4rem',marginTop:'0.65rem',flexWrap:'wrap'}}>
                <button onClick={imprimirHistoriaCompleta} style={{...btn('print',true),background:'rgba(255,255,255,0.15)',color:'#fff',border:'1px solid rgba(255,255,255,0.3)'}}><Printer size={13}/> Historia completa</button>
                <button onClick={()=>{ setFEditPac({...detPac}); setMEditPac(true); }} style={{...btn('ghost',true),color:'#fff',border:'1px solid rgba(255,255,255,0.3)',background:'rgba(255,255,255,0.1)'}}>Editar</button>
              </div>
            </div>

            <div ref={printRef} style={{overflowY:'auto',padding:'1.1rem',flex:1,display:'flex',flexDirection:'column',gap:'1rem'}}>

              {/* ── Resumen KPIs ── */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'0.5rem'}}>
                {[
                  {l:'Sesiones',     v:detPac.total_sesiones??0,       color:C.teal},
                  {l:'Costo/sesión', v:fmt(detPac.costo_sesion??80000), color:C.ocre},
                  {l:'Total pagado', v:fmt(detPac.total_pagado??0),     color:C.success},
                ].map(({l,v,color})=>(
                  <div key={l} style={{background:C.crema,borderRadius:10,padding:'0.55rem 0.75rem',border:`1px solid ${C.border}`,textAlign:'center'}}>
                    <div style={{fontSize:'0.63rem',color:C.gray,fontWeight:700,textTransform:'uppercase'}}>{l}</div>
                    <div style={{fontWeight:700,color,fontSize:'0.95rem',marginTop:'0.1rem'}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* ── Sección clínica (siempre visible) ── */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.55rem'}}>
                <div style={{background:C.ocreLight,border:`1px solid ${C.border}`,borderRadius:10,padding:'0.7rem 0.9rem'}}>
                  <div style={{fontSize:'0.63rem',color:C.ocreDark,fontWeight:700,textTransform:'uppercase',marginBottom:'0.3rem'}}>Diagnóstico</div>
                  <div style={{color:C.teal,fontWeight:600,fontSize:'0.84rem',whiteSpace:'pre-wrap'}}>{detPac.diagnostico||<span style={{color:C.gray,fontStyle:'italic',fontWeight:400}}>Sin registrar</span>}</div>
                </div>
                <div style={{background:C.crema,border:`1px solid ${C.border}`,borderRadius:10,padding:'0.7rem 0.9rem'}}>
                  <div style={{fontSize:'0.63rem',color:C.gray,fontWeight:700,textTransform:'uppercase',marginBottom:'0.3rem'}}>Motivo de consulta</div>
                  <div style={{color:C.teal,fontSize:'0.84rem',whiteSpace:'pre-wrap'}}>{detPac.motivo_consulta||<span style={{color:C.gray,fontStyle:'italic',fontWeight:400}}>Sin registrar</span>}</div>
                </div>
                <div style={{background:C.tealLight,border:`1px solid ${C.teal}22`,borderRadius:10,padding:'0.7rem 0.9rem',gridColumn:'1/-1'}}>
                  <div style={{fontSize:'0.63rem',color:C.teal,fontWeight:700,textTransform:'uppercase',marginBottom:'0.3rem'}}>Resumen de la atención</div>
                  <div style={{color:C.teal,fontSize:'0.84rem',whiteSpace:'pre-wrap'}}>{detPac.notas||<span style={{color:C.gray,fontStyle:'italic',fontWeight:400}}>Sin registrar</span>}</div>
                </div>
              </div>

              {/* ── Datos personales ── */}
              <div>
                <div style={{fontSize:'0.72rem',fontWeight:700,color:C.gray,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'0.45rem'}}>Datos personales</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'0.4rem'}}>
                  {[
                    {l:'Cédula',          v:detPac.cedula},
                    {l:'Email',           v:detPac.email,   wide:true},
                    {l:'Teléfono',        v:detPac.telefono},
                    {l:'Fecha nac.',      v:fmtFecha(detPac.fecha_nacimiento)},
                    {l:'Género',          v:detPac.genero},
                    {l:'Estado civil',    v:detPac.estado_civil},
                    {l:'Ocupación',       v:detPac.ocupacion},
                    {l:'Educación',       v:detPac.educacion},
                    {l:'País',            v:detPac.pais},
                    {l:'Departamento',    v:detPac.departamento},
                    {l:'Ciudad',          v:detPac.ciudad},
                    {l:'Estado',          v:detPac.estado==='formulario_pendiente'?'por revisar':detPac.estado},
                    {l:'Contacto emerg.', v:detPac.contacto_emergencia},
                    {l:'Tel. emergencia', v:detPac.telefono_emergencia},
                  ].map(({l,v,wide})=>(
                    <div key={l} style={{background:C.crema,borderRadius:8,padding:'0.4rem 0.65rem',border:`1px solid ${C.border}`,gridColumn:wide?'span 2':undefined}}>
                      <div style={{fontSize:'0.62rem',color:C.gray,fontWeight:700,textTransform:'uppercase'}}>{l}</div>
                      <div style={{fontWeight:600,color:C.teal,fontSize:'0.82rem',wordBreak:'break-word'}}>{v||'—'}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Documentos ── */}
              <div style={{marginTop:'0.5rem',marginBottom:'1.2rem'}}>
                <div style={{fontWeight:700,color:C.teal,marginBottom:'0.6rem',fontSize:'0.88rem',textTransform:'uppercase',letterSpacing:'0.04em',display:'flex',alignItems:'center',gap:'0.4rem'}}>
                  <FileCheck size={14}/> Documentos
                </div>
                <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'0.65rem'}}>
                  {[{tipo:'consentimiento',label:'Consentimiento informado'},{tipo:'plan_trabajo',label:'Plan de trabajo'}].map(({tipo,label})=>{
                    const doc = detPac.documentos?.find(d=>d.tipo===tipo);
                    return(
                      <div key={tipo} style={{background:doc?'#F0FDF4':C.crema,borderRadius:10,padding:'0.85rem 1rem',border:`1.5px solid ${doc?'#86EFAC':C.border}`,display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'0.4rem'}}>
                          <FileCheck size={14} color={doc?C.success:C.gray}/>
                          <span style={{fontWeight:600,color:C.teal,fontSize:'0.82rem'}}>{label}</span>
                        </div>
                        {doc?(
                          <>
                            <div style={{fontSize:'0.72rem',color:C.teal,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:'pointer',textDecoration:'underline dotted'}}
                              onClick={()=>verDocumento(doc.id,doc.nombre_original)}>
                              {doc.nombre_original}
                            </div>
                            <div style={{display:'flex',gap:'0.4rem'}}>
                              <button style={{...btn('teal',true),flex:1,justifyContent:'center'}} onClick={()=>verDocumento(doc.id,doc.nombre_original)}>
                                <FileCheck size={12}/> Ver
                              </button>
                              <button style={btn('ghost',true)} onClick={()=>descargarDocumento(doc.id,doc.nombre_original)} title="Descargar">
                                <Download size={12}/>
                              </button>
                              <button style={btn('danger',true)} onClick={()=>eliminarDocumento(doc.id,detPac.id)}>
                                <Trash2 size={12}/>
                              </button>
                            </div>
                          </>
                        ):(
                          <label style={{...btn('ghost',true),cursor:'pointer',justifyContent:'center',display:'flex',alignItems:'center',gap:'0.4rem'}}>
                            <Upload size={12}/> Subir PDF
                            <input type="file" accept="application/pdf" style={{display:'none'}}
                              onChange={e=>{ if(e.target.files[0]) subirDocumento(detPac.id,tipo,e.target.files[0]); e.target.value=''; }}/>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sesiones */}
              {detPac.sesiones?.length>0&&(
                <div style={{marginTop:'1.2rem'}}>
                  <div style={{fontWeight:700,color:C.teal,marginBottom:'0.8rem',fontSize:'0.88rem',textTransform:'uppercase',letterSpacing:'0.04em',display:'flex',alignItems:'center',gap:'0.4rem'}}>
                    <Activity size={14}/> Historial de sesiones ({detPac.sesiones.length})
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'0.8rem'}}>
                    {detPac.sesiones.map((s,i)=>(
                      <div key={s.id} style={{background:C.white,borderRadius:14,padding:'1.2rem',border:`1px solid ${C.border}`,boxShadow:'0 4px 12px rgba(45,79,74,0.08)',transition:'all 0.3s ease',position:'relative',overflow:'hidden',borderLeft:`4px solid ${C.teal}`}}>
                        {/* Header */}
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'0.9rem'}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:700,color:C.teal,fontSize:'0.95rem'}}>Sesión {i+1}</div>
                            <div style={{fontWeight:600,color:C.ocre,fontSize:'0.85rem',marginTop:'0.2rem'}}>{s.tema}</div>
                            <div style={{fontSize:'0.78rem',color:C.gray,marginTop:'0.25rem',display:'flex',gap:'1rem'}}>
                              <span>📅 {fmtFecha(s.fecha)}</span>
                              <span>⏱ {s.duracion} min</span>
                            </div>
                          </div>
                          <button onClick={()=>imprimirSesion(s,i)} style={{...btn('print',true),flexShrink:0,transition:'all 0.2s'}}><Printer size={13}/></button>
                        </div>

                        {/* Grid de datos */}
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.8rem',fontSize:'0.82rem',marginBottom:'0.9rem',paddingBottom:'0.9rem',borderBottom:`1px solid ${C.border}33`}}>
                          {s.estado_emocional&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Estado emocional</div>
                              <span style={{background:`linear-gradient(135deg,${C.ocreLight},#FEF3C7)`,color:C.ocreDark,padding:'0.25rem 0.7rem',borderRadius:8,display:'inline-block',fontSize:'0.76rem',fontWeight:600,border:`1px solid ${C.ocre}33`}}>{s.estado_emocional.replace('_',' ')}</span>
                            </div>
                          )}
                          {s.tecnicas_utilizadas&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Técnicas</div>
                              <div style={{color:C.gray,fontSize:'0.82rem'}}>{s.tecnicas_utilizadas}</div>
                            </div>
                          )}
                          {s.diagnosticos&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Diagnósticos CIE10</div>
                              <div style={{color:C.gray,fontSize:'0.78rem',lineHeight:'1.3'}}>{s.diagnosticos.split(' | ').filter(d=>d && d!=='dummy_end').join(', ')}</div>
                            </div>
                          )}
                          {s.proxima_sesion_recomendada&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Próxima sesión</div>
                              <div style={{color:C.gray,fontWeight:600}}>{fmtFecha(s.proxima_sesion_recomendada)}</div>
                            </div>
                          )}
                        </div>

                        {/* Descripción */}
                        <div style={{display:'flex',flexDirection:'column',gap:'0.7rem'}}>
                          {s.objetivos_sesion&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Cuadro clínico de atención</div>
                              <div style={{color:C.gray,fontSize:'0.82rem',lineHeight:'1.5',padding:'0.5rem 0.75rem',background:C.crema,borderRadius:8,borderLeft:`3px solid ${C.teal}`}}>{s.objetivos_sesion}</div>
                            </div>
                          )}
                          {s.notas&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Resumen de la atención</div>
                              <div style={{color:C.gray,fontSize:'0.82rem',lineHeight:'1.5',padding:'0.5rem 0.75rem',background:C.crema,borderRadius:8,borderLeft:`3px solid ${C.ocre}`}}>{s.notas}</div>
                            </div>
                          )}
                          {s.tareas_asignadas&&(
                            <div>
                              <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.03em',marginBottom:'0.2rem'}}>Tareas y recomendaciones</div>
                              <div style={{background:'linear-gradient(135deg,#FFFBEB,#FEF3C7)',border:`1.5px solid #FCD34D`,borderRadius:8,padding:'0.6rem 0.85rem',color:'#92400E',fontSize:'0.82rem',lineHeight:'1.5',fontWeight:500}}>{s.tareas_asignadas}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pagos */}
              {detPac.pagos?.length>0&&(
                <div style={{marginTop:'1.2rem'}}>
                  <div style={{fontWeight:700,color:C.teal,marginBottom:'0.6rem',fontSize:'0.88rem',textTransform:'uppercase',letterSpacing:'0.04em',display:'flex',alignItems:'center',gap:'0.4rem'}}>
                    <DollarSign size={14}/> Pagos ({detPac.pagos.length})
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'0.4rem'}}>
                    {detPac.pagos.map(p=>(
                      <div key={p.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:C.crema,borderRadius:9,padding:'0.5rem 0.85rem',border:`1px solid ${C.border}`}}>
                        <div><span style={{fontWeight:600,color:C.teal,fontSize:'0.84rem'}}>{fmtFecha(p.fecha)}</span><span style={{fontSize:'0.77rem',color:C.gray,marginLeft:'0.5rem',textTransform:'capitalize'}}>{p.metodo}</span>{p.notas&&<span style={{fontSize:'0.74rem',color:C.gray,marginLeft:'0.4rem'}}>· {p.notas}</span>}</div>
                        <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                          <span style={{fontWeight:700,color:C.success}}>{fmt(p.monto)}</span>
                          <span style={badge('green')}>{p.estado}</span>
                          <button style={btn('danger',true)} onClick={async()=>{ await eliminarPago(p.id); await cargarDetPac(detPac.id); }}><Trash2 size={12}/></button>
                        </div>
                      </div>
                    ))}
                    <div style={{display:'flex',justifyContent:'flex-end',padding:'0.5rem 0.85rem',background:C.ocreLight,borderRadius:9,border:`1px solid ${C.border}`}}>
                      <span style={{fontWeight:700,color:C.teal}}>Total: </span>
                      <span style={{fontWeight:700,color:C.success,marginLeft:'0.5rem'}}>{fmt(detPac.total_pagado??0)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Acciones */}
            <div style={{padding:'1rem 1.5rem',borderTop:`1px solid ${C.border}`,display:'flex',gap:'0.6rem',background:C.crema,borderRadius:'0 0 16px 16px',flexShrink:0,flexWrap:'wrap'}}>
              {detPac.whatsapp&&<a href={`https://wa.me/57${detPac.whatsapp.replace(/^\+/,'').replace(/^57/,'')}`} target="_blank" rel="noopener noreferrer" style={btn('whatsapp')}><MessageCircle size={14}/> WhatsApp</a>}
              <button style={btn('teal')} onClick={()=>{ setSelPac(detPac); setSelCita(null); setMHistoria(false); setMSesion(true); }}><FileText size={14}/> Nueva sesión</button>
              <button style={btn('primary')} onClick={()=>{ setSelPac(detPac); setMHistoria(false); setMPago(true); }}><DollarSign size={14}/> Registrar pago</button>
              <button onClick={()=>setMHistoria(false)} style={{...btn('ghost'),marginLeft:'auto'}}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Paciente */}
      {mEditPac&&(
        <Modal title="Editar paciente" onClose={()=>setMEditPac(false)} color={C.ocre} wide>
          <form onSubmit={guardarEditPac}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <F label="Nombre *"><input style={inp} value={fEditPac.nombre||''} onChange={e=>setFEditPac({...fEditPac,nombre:e.target.value})} required/></F>
              <F label="Apellido *"><input style={inp} value={fEditPac.apellido||''} onChange={e=>setFEditPac({...fEditPac,apellido:e.target.value})} required/></F>
              <F label="Cédula"><input style={inp} value={fEditPac.cedula||''} onChange={e=>setFEditPac({...fEditPac,cedula:e.target.value})} placeholder="Número de identificación"/></F>
              <F label="Email"><input style={inp} type="email" value={fEditPac.email||''} onChange={e=>setFEditPac({...fEditPac,email:e.target.value})}/></F>
              <F label="WhatsApp"><input style={inp} value={fEditPac.whatsapp||''} onChange={e=>setFEditPac({...fEditPac,whatsapp:e.target.value})} placeholder="+573001234567"/></F>
              <F label="Fecha de nacimiento"><input style={inp} type="date" value={fEditPac.fecha_nacimiento||''} onChange={e=>setFEditPac({...fEditPac,fecha_nacimiento:e.target.value})}/></F>
              <F label="Género">
                <select style={inp} value={fEditPac.genero||''} onChange={e=>setFEditPac({...fEditPac,genero:e.target.value})}>
                  <option value="">Seleccionar...</option>
                  <option value="femenino">Femenino</option>
                  <option value="masculino">Masculino</option>
                  <option value="otro">Otro</option>
                </select>
              </F>
              <F label="Estado">
                <select style={inp} value={fEditPac.estado||'activo'} onChange={e=>setFEditPac({...fEditPac,estado:e.target.value})}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="pausa">En pausa</option>
                  <option value="alta">Alta</option>
                </select>
              </F>
              <F label="Ocupación"><input style={inp} value={fEditPac.ocupacion||''} onChange={e=>setFEditPac({...fEditPac,ocupacion:e.target.value})}/></F>
              <F label="Costo por sesión"><input style={inp} type="number" value={fEditPac.costo_sesion||''} onChange={e=>setFEditPac({...fEditPac,costo_sesion:e.target.value})}/></F>
              <F label="Contacto de emergencia"><input style={inp} value={fEditPac.contacto_emergencia||''} onChange={e=>setFEditPac({...fEditPac,contacto_emergencia:e.target.value})}/></F>
              <F label="Teléfono de emergencia"><input style={inp} value={fEditPac.telefono_emergencia||''} onChange={e=>setFEditPac({...fEditPac,telefono_emergencia:e.target.value})}/></F>
            </div>
            <F label="Dirección"><input style={inp} value={fEditPac.direccion||''} onChange={e=>setFEditPac({...fEditPac,direccion:e.target.value})}/></F>
            <F label="Diagnóstico DSM-5">
              <DSM5Picker value={fEditPac.diagnostico||''} onChange={v=>setFEditPac({...fEditPac,diagnostico:v})} inp={inp} C={C}/>
            </F>
            <F label="Motivo de consulta"><textarea style={{...inp,height:70,resize:'vertical'}} value={fEditPac.motivo_consulta||''} onChange={e=>setFEditPac({...fEditPac,motivo_consulta:e.target.value})}/></F>
            <F label="Resumen de la atención"><textarea style={{...inp,height:70,resize:'vertical'}} value={fEditPac.notas||''} onChange={e=>setFEditPac({...fEditPac,notas:e.target.value})}/></F>
            <div style={{display:'flex',gap:'0.75rem',marginTop:'0.5rem'}}>
              <button type="button" onClick={()=>setMEditPac(false)} style={btn('ghost')}>Cancelar</button>
              <button type="submit" style={{...btn('primary'),flex:1}}>Guardar cambios</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Visor PDF ── */}
      {pdfVisor&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',display:'flex',flexDirection:'column',zIndex:2000}}>
          {/* Barra superior */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.65rem 1rem',background:C.teal,flexShrink:0}}>
            <span style={{color:'#fff',fontWeight:600,fontSize:'0.9rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'calc(100% - 120px)'}}>{pdfVisor.nombre}</span>
            <div style={{display:'flex',gap:'0.5rem',flexShrink:0}}>
              <button style={btn('print',true)} onClick={()=>descargarDocumento(pdfVisor.docId,pdfVisor.nombre)}>
                <Download size={13}/> Descargar
              </button>
              <button onClick={()=>{ URL.revokeObjectURL(pdfVisor.url); setPdfVisor(null); }}
                style={{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',borderRadius:8,cursor:'pointer',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',width:32,height:32}}>
                <X size={17}/>
              </button>
            </div>
          </div>
          {/* iframe */}
          <iframe
            src={pdfVisor.url}
            title={pdfVisor.nombre}
            style={{flex:1,border:'none',width:'100%'}}
          />
        </div>
      )}
    </div>
  );
}
